// Session 147j+6 — LODR Order-Book scanner admin tab.
// Real-time SEBI Reg 30 (LODR) "Award_of_Order_Receipt_of_Order"
// announcements from BSE, with LLM-extracted structured fields.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FileText, RefreshCw, Activity, Filter, ExternalLink, AlertCircle,
  CheckCircle2, Clock, IndianRupee, TrendingUp, Search, Wrench, X,
  ChevronRight, ChevronDown, ChevronLeft,
  Pause, Play, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const tok = localStorage.getItem(TOKEN_KEY);
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const fmtCr = (v) => (v == null ? '—' : `₹${Number(v).toFixed(1)} Cr`);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }); } catch { return iso.slice(0, 16); }
};

const customerTypeTone = {
  GOVERNMENT_DEFENCE: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10',
  GOVERNMENT_OTHER: 'border-blue-500/40 text-blue-300 bg-blue-500/10',
  PSU: 'border-cyan-500/40 text-cyan-300 bg-cyan-500/10',
  PRIVATE_DOMESTIC: 'border-amber-500/40 text-amber-300 bg-amber-500/10',
  PRIVATE_INTERNATIONAL: 'border-violet-500/40 text-violet-300 bg-violet-500/10',
  UNKNOWN: 'border-border text-muted-foreground bg-surface-highlight',
};

const orderTypeTone = {
  ORDER: 'border-emerald-500/50 text-emerald-300 bg-emerald-500/10',
  LOI: 'border-amber-500/50 text-amber-300 bg-amber-500/10',
  EXECUTION_UPDATE: 'border-border text-muted-foreground bg-surface-highlight',
};

// ==================================================================
// Bullishness cell — Session 147j+16ag (Deep Analysis Phase 2)
// Compactly displays the LLM-derived short/long-term scores, catalyst
// tag, and risk flags via a tooltip. Renders an em-dash when the row
// hasn't been scored yet (sub-₹1 Cr or backfill pending).
// ==================================================================
const catalystTone = {
  BREAKOUT:    'border-fuchsia-400/60 text-fuchsia-200 bg-fuchsia-500/10',
  INCREMENTAL: 'border-sky-400/60 text-sky-200 bg-sky-500/10',
  MAINTENANCE: 'border-zinc-400/40 text-zinc-300 bg-zinc-500/10',
  BEARISH:     'border-rose-400/60 text-rose-200 bg-rose-500/10',
};

const scoreColor = (score) => {
  if (score == null || score < 0) return 'text-muted-foreground';
  if (score >= 8) return 'text-emerald-300';
  if (score >= 5) return 'text-amber-300';
  return 'text-rose-300';
};

// Verdict tone — used both in BullishnessCell and DeepAnalysisCard.
// 147j+16ch — "LOW IMPACT" added for the deterministic Minor bundle
// (clearer than the earlier "WATCH" which read as a positive cue).
// "WATCH" kept as an alias for any legacy rows already written.
const verdictTone = {
  BUY:          'bg-emerald-500 text-white border-emerald-400',
  HOLD:         'bg-sky-500 text-white border-sky-400',
  WATCH:        'bg-amber-500 text-white border-amber-400',
  'LOW IMPACT': 'bg-zinc-500 text-white border-zinc-400',
  SKIP:         'bg-zinc-600 text-white border-zinc-500',
  AVOID:        'bg-rose-600 text-white border-rose-500',
};

const BullishnessCell = ({ row }) => {
  const p2 = row?.deep_analysis?.phase2;
  if (!p2) {
    return (
      <td
        className="px-3 py-2 text-center text-muted-foreground"
        title="Bullishness score not yet computed. Order < ₹1 Cr OR Phase 2 backfill pending."
        data-testid={`lodr-bullishness-empty-${row._id}`}
      >
        —
      </td>
    );
  }
  const s = p2.short_term_score;
  const l = p2.long_term_score;
  const cs = (p2.catalyst_strength || '').toUpperCase();
  const verdict = (p2.verdict || '').toUpperCase();
  // 147j+16cs — Red flags. Deterministic Layer A + optional Gemini Layer B.
  const flags = p2.red_flags || [];
  const inspector = p2.red_flag_inspector || null;
  const hasHigh = flags.some(f => f.severity === 'high') || (inspector?.red_flag_score ?? 0) >= 7;
  const hasWarn = flags.some(f => f.severity === 'warn') || (inspector?.red_flag_score ?? 0) >= 4;
  const flagCount = flags.length + (inspector ? 1 : 0);
  const flagTip = [
    ...flags.map(f => `${f.label}: ${f.detail}`),
    inspector?.one_line_summary
      ? `🔍 Inspector ${inspector.red_flag_score}/10: ${inspector.one_line_summary}`
      : '',
  ].filter(Boolean).join('\n');
  const tip = [
    p2.verdict_reason || p2.rationale_short || '',
    cs ? `Catalyst: ${cs}` : '',
    (p2.risk_flags || []).length
      ? `Risks: ${(p2.risk_flags || []).join(', ')}`
      : '',
    p2.earnings_visibility_months != null
      ? `P&L visibility: ~${p2.earnings_visibility_months} mo`
      : '',
  ].filter(Boolean).join(' · ');
  return (
    <td
      className="px-3 py-2 text-center"
      title={tip || undefined}
      data-testid={`lodr-bullishness-cell-${row._id}`}
    >
      <div className="flex flex-col items-center gap-1">
        {verdict && (
          <span
            className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border rounded ${verdictTone[verdict] || verdictTone.SKIP}`}
            data-testid={`lodr-verdict-pill-${row._id}`}
          >
            {verdict}
          </span>
        )}
        <div className="flex items-center gap-1 font-mono text-[11px]">
          <span title="Short-term (1-3 day)" className={scoreColor(s)}>S {s}</span>
          <span className="text-muted-foreground">·</span>
          <span title="Long-term (FY27 P&L)" className={scoreColor(l)}>L {l}</span>
        </div>
        {cs && (
          <span
            className={`px-1 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm ${catalystTone[cs] || catalystTone.MAINTENANCE}`}
            data-testid={`lodr-catalyst-${cs.toLowerCase()}`}
          >
            {cs}
          </span>
        )}
        {/* 147j+16cs — Red-flag badge. Click to expand the row drawer
            (the full breakdown lives in the Deep Analysis card). */}
        {flagCount > 0 && (
          <span
            className={
              `mt-0.5 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border rounded-sm cursor-help ` +
              (hasHigh
                ? 'border-rose-500/70 bg-rose-500/15 text-rose-200'
                : hasWarn
                  ? 'border-amber-500/70 bg-amber-500/15 text-amber-200'
                  : 'border-sky-500/70 bg-sky-500/15 text-sky-200')
            }
            title={flagTip}
            data-testid={`lodr-redflag-badge-${row._id}`}
          >
            ⚠ {flagCount} FLAG{flagCount === 1 ? '' : 'S'}
            {inspector ? ` · ${inspector.red_flag_score}/10` : ''}
          </span>
        )}
      </div>
    </td>
  );
};

// ==================================================================
// Deep Analysis card — Session 147j+16ai (enriched schema)
// 3-section layout:
//   1) VERDICT HERO strip (verdict pill · scores · catalyst · key chip)
//   2) THESIS + BULL/BEAR cases (sans-serif prose, readable)
//   3) DETERMINISTIC context + risk flags
// Renders gracefully when phase2/phase1 are missing.
// ==================================================================
const sectorTone = {
  STRONG:  'text-emerald-300',
  NEUTRAL: 'text-zinc-300',
  WEAK:    'text-rose-300',
};

const marginTone = {
  ACCRETIVE: 'text-emerald-300',
  NEUTRAL:   'text-zinc-300',
  DILUTIVE:  'text-rose-300',
};

// Session 147j+16bk — Per-flag tone map for the LODR risk-flag chips.
//   ROSE  → balance-sheet stress / governance red flag (negative)
//   SKY   → POSITIVE signal (market-moving size) — same chip area but
//           styled to differentiate from genuine risks
//   AMBER → informational / soft caution
// Any flag not in the map falls back to the rose default so unknown
// LLM-emitted flags stay visible without breaking the layout.
const RISK_FLAG_TONE = {
  // Phase B (deterministic, balance-sheet) — red
  leveraged:           'rose',
  low_icr:             'rose',
  pledge_high:         'rose',
  promoter_selling:    'rose',
  cashflow_weak:       'rose',
  // Phase B (deterministic, positive) — sky
  market_moving_size:  'sky',
  // Pre-existing LLM-emitted risk flags — keep current rose tone
  execution_risk:          'rose',
  customer_concentration:  'rose',
  margin_compression:      'rose',
  govt_payment_delay:      'amber',
  currency_risk:           'amber',
  jv_dilution:             'rose',
  approx_value:            'amber',
  short_dated:             'amber',
  margin_unknown:          'amber',
};

const RISK_FLAG_CHIP_CLASSES = {
  rose:  'border-rose-400/50 text-rose-200 bg-rose-500/10',
  sky:   'border-sky-400/60 text-sky-200 bg-sky-500/10',
  amber: 'border-amber-400/50 text-amber-200 bg-amber-500/10',
};

const ScoreBar = ({ score, label, hint }) => {
  const s = Number.isFinite(score) ? Math.max(0, Math.min(10, score)) : null;
  return (
    <div className="flex items-center gap-2" title={hint}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground w-16">
        {label}
      </div>
      <div className="flex-1 h-1.5 bg-surface-highlight rounded-sm overflow-hidden">
        {s != null && (
          <div
            className={`h-full ${s >= 8 ? 'bg-emerald-400' : s >= 5 ? 'bg-amber-400' : 'bg-rose-400'}`}
            style={{ width: `${(s / 10) * 100}%` }}
          />
        )}
      </div>
      <div className={`font-mono text-[11px] font-bold w-9 text-right ${scoreColor(s)}`}>
        {s != null ? `${s}/10` : '—'}
      </div>
    </div>
  );
};

// 147j+16aw — Compact momentum bar for the drawer. Renders only
// when the row has at least one prior order in the 30-day window
// (otherwise it'd just say "no priors" which is noise).
const MomentumSection = ({ phase1, rowId }) => {
  const m = phase1?.momentum || {};
  const w30 = m['30d'] || null;
  const w90 = m['90d'] || null;
  const hasAny = (w30?.count > 0) || (w90?.count > 0);
  if (!hasAny) return null;

  const fmtCr = (v) => {
    if (v == null) return '—';
    if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K Cr`;
    return `₹${v.toFixed(0)} Cr`;
  };
  const streakLabel = (count) => {
    if (count >= 4) return { emoji: '🔥', text: 'BLAZING STREAK', tone: 'text-orange-400' };
    if (count >= 2) return { emoji: '📈', text: 'HOT STREAK', tone: 'text-amber-400' };
    if (count === 1) return { emoji: '·', text: 'RECENT WIN', tone: 'text-sky-400' };
    return null;
  };
  const streak = streakLabel(w30?.count || 0);

  return (
    <div
      className="rounded-md border border-border bg-surface-highlight/50 p-3 space-y-2"
      data-testid={`lodr-momentum-section-${rowId}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Order velocity
        </div>
        {streak && (
          <span
            className={`text-[10px] font-bold tracking-wider ${streak.tone}`}
            data-testid={`lodr-streak-badge-${rowId}`}
          >
            {streak.emoji} {streak.text}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 text-[12px]">
        {/* 30-day window */}
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
            Last 30 days
          </div>
          <div className="font-mono text-foreground">
            <span className="font-bold">{w30?.count || 0}</span> orders ·{' '}
            <span className="text-foreground/80">{fmtCr(w30?.total_value_cr)}</span>
          </div>
          <div className="text-[11px] text-foreground/70">
            {w30?.unique_customers || 0} distinct customer{w30?.unique_customers === 1 ? '' : 's'}
            {w30?.days_since_last != null && (
              <> · {w30.days_since_last}d since last</>
            )}
            {w30?.value_pct_of_ttm != null && (
              <> · {w30.value_pct_of_ttm}% of TTM</>
            )}
          </div>
        </div>
        {/* 90-day window */}
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
            Last 90 days
          </div>
          <div className="font-mono text-foreground">
            <span className="font-bold">{w90?.count || 0}</span> orders ·{' '}
            <span className="text-foreground/80">{fmtCr(w90?.total_value_cr)}</span>
          </div>
          <div className="text-[11px] text-foreground/70">
            {w90?.unique_customers || 0} distinct customer{w90?.unique_customers === 1 ? '' : 's'}
            {w90?.value_pct_of_ttm != null && (
              <> · {w90.value_pct_of_ttm}% of TTM</>
            )}
          </div>
        </div>
      </div>
      {w30?.same_customer_count > 0 && (
        <div
          className="text-[11px] text-amber-300/80 border-t border-border/40 pt-2"
          data-testid={`lodr-same-customer-flag-${rowId}`}
        >
          ⚠ {w30.same_customer_count} prior order{w30.same_customer_count === 1 ? '' : 's'} from the SAME customer — single-client concentration risk.
        </div>
      )}
    </div>
  );
};


// ==================================================================
// Session 147j+16bp — Smart-Money Activity (bulk + block deal overlay).
// Lazy-loads via /api/admin/lodr/bulk-deals/{symbol} so the /orders
// payload stays small. Placed in the drawer BETWEEN deterministic
// context (momentum/fundamentals) and the qualitative thesis/bull/bear
// section — Smart Money is factual evidence, not opinion, so it
// belongs with the facts.
// ==================================================================
const _SM_CATEGORY_TONE = {
  HNI: 'border-emerald-400/60 text-emerald-200 bg-emerald-500/10',
  MF:  'border-sky-400/60 text-sky-200 bg-sky-500/10',
  FII: 'border-violet-400/60 text-violet-200 bg-violet-500/10',
  INS: 'border-cyan-400/60 text-cyan-200 bg-cyan-500/10',
  PMS: 'border-amber-400/60 text-amber-200 bg-amber-500/10',
};

const _fmtSharesCompact = (n) => {
  if (n == null) return '—';
  const v = Math.abs(Number(n));
  if (v >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${n}`;
};

const _fmtOffset = (n) => {
  if (n == null) return '';
  if (n === 0) return 'T+0';
  return n > 0 ? `T+${n}` : `T${n}`;
};

const SmartMoneySection = ({ row }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const symbol = row?.nse_symbol;
  const announcementDate = row?.announcement_date;
  // We trust the batched summary attached to the row when present; only
  // fetch deal detail (the heavy `deals` array) if the summary signals
  // matches exist. Avoids any wasted XHR on the 90% of rows that have
  // no smart-money activity.
  const summary = row?.smart_money;
  const hasMatches = (summary?.matched_count || 0) > 0;

  useEffect(() => {
    if (!hasMatches || !symbol || !announcementDate) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(
      `${API}/api/admin/lodr/bulk-deals/${encodeURIComponent(symbol)}`,
      {
        headers: authHeaders(),
        params: {
          announcement_date: announcementDate,
          window_days: summary?.window_days || 5,
        },
      },
    )
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, announcementDate, hasMatches, summary?.window_days]);

  if (!hasMatches) return null;

  const deals = data?.deals || [];
  const net = data?.net_qty ?? summary?.net_qty ?? 0;
  const dominant = net > 0 ? 'BUY' : net < 0 ? 'SELL' : 'MIXED';
  const dominantTone =
    dominant === 'BUY' ? 'text-emerald-300'
    : dominant === 'SELL' ? 'text-rose-300'
    : 'text-muted-foreground';

  return (
    <div
      className="rounded-md border border-border bg-surface-highlight/50 p-3 space-y-2"
      data-testid={`lodr-smart-money-section-${row._id}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold flex items-center gap-1.5">
          <span>💰</span> Smart Money Activity
          <span className="text-muted-foreground font-normal normal-case tracking-normal">
            · ±{summary?.window_days || 5}d
          </span>
        </div>
        <div className={`text-[11px] font-mono font-bold ${dominantTone}`}>
          NET {dominant === 'BUY' ? '+' : dominant === 'SELL' ? '−' : ''}
          {_fmtSharesCompact(Math.abs(net))} shares
        </div>
      </div>

      {loading && (
        <div className="text-[11px] text-muted-foreground italic">Loading deals…</div>
      )}
      {error && (
        <div className="text-[11px] text-rose-300">Failed: {error}</div>
      )}

      {!loading && !error && deals.length === 0 && summary?.matched_count > 0 && (
        // Summary said there are matches but the lazy fetch came back empty.
        // Surface the batched summary so the user isn't left with a blank section.
        <div className="text-[11px] text-foreground/80 font-mono">
          {summary.matched_count} smart-money deal{summary.matched_count === 1 ? '' : 's'} —{' '}
          top entity{' '}
          <span className="text-foreground">{summary.top_name}</span>{' '}
          {summary.top_side} {_fmtSharesCompact(summary.top_qty)} ({_fmtOffset(summary.top_offset_days)})
        </div>
      )}

      {!loading && deals.length > 0 && (
        <div className="space-y-1.5">
          {deals.map((d, i) => {
            const tone = _SM_CATEGORY_TONE[d.smart_money_category] || _SM_CATEGORY_TONE.HNI;
            const sideTone = d.side === 'BUY' ? 'text-emerald-300' : 'text-rose-300';
            return (
              <div
                key={d._id || i}
                className="grid grid-cols-[1fr_auto] gap-2 items-center text-[12px] border-b border-border/30 last:border-0 pb-1.5 last:pb-0"
                data-testid={`lodr-smart-money-deal-${row._id}-${i}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`px-1 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm ${tone}`}
                      title={`Category: ${d.smart_money_category}`}
                    >
                      {d.smart_money_category}
                    </span>
                    <span className="font-bold text-foreground truncate">
                      {d.smart_money_alias}
                    </span>
                    <span className={`font-mono text-[10px] ${sideTone}`}>
                      {d.side}
                    </span>
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {_fmtOffset(d.t_offset)}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                    {_fmtSharesCompact(d.qty)} sh @ ₹{d.avg_price?.toFixed(2)}{' '}
                    · {d.exchange} {d.deal_type}{' '}
                    · {d.trade_date}
                  </div>
                </div>
                <div className="text-right font-mono text-[11px] text-foreground/85">
                  ₹{d.notional_cr?.toFixed(2)} Cr
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// Session 147j+17c — Liquidity panel: fetches the symbol_liquidity_snapshot
// for the row's symbol so a trader can immediately see whether the stock
// is even tradeable at size before acting on the BUY verdict. Renders
// tier chip (LIQUID / NORMAL / THIN / ILLIQUID / GHOST), 30-day avg
// turnover + volume, today's volume ratio, delivery-% collapse flag,
// and days-to-absorb (order value ÷ avg daily turnover).
const LiquidityPanel = ({ symbol, row }) => {
  const [snap, setSnap] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/admin/lodr/liquidity/symbol/${encodeURIComponent(symbol)}`,
          { headers: authHeaders(), cache: 'no-store' },
        );
        if (!r.ok) {
          if (!cancelled) setErr(r.status === 404 ? 'No liquidity snapshot yet — run Liquidity scan in admin panel.' : `HTTP ${r.status}`);
          return;
        }
        const d = await r.json();
        if (!cancelled) setSnap(d);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [symbol]);

  if (loading) {
    return (
      <div className="text-[11px] text-zinc-500 italic" data-testid="lodr-liquidity-loading">
        Loading liquidity snapshot…
      </div>
    );
  }
  if (err) {
    return (
      <div
        className="text-[11px] text-zinc-500 italic border border-border/60 bg-surface/40 rounded px-2.5 py-1.5"
        data-testid="lodr-liquidity-error"
      >
        Liquidity data unavailable — {err}
      </div>
    );
  }
  if (!snap) return null;

  const tierPalette = {
    LIQUID:   { border: 'border-emerald-500/50', bg: 'bg-emerald-500/5', accent: 'text-emerald-300', label: 'LIQUID' },
    NORMAL:   { border: 'border-sky-500/40',     bg: 'bg-sky-500/5',     accent: 'text-sky-300',     label: 'NORMAL' },
    THIN:     { border: 'border-amber-500/50',   bg: 'bg-amber-500/5',   accent: 'text-amber-300',   label: 'THIN' },
    ILLIQUID: { border: 'border-orange-500/60',  bg: 'bg-orange-500/8',  accent: 'text-orange-300',  label: 'ILLIQUID' },
    GHOST:    { border: 'border-rose-500/60',    bg: 'bg-rose-500/8',    accent: 'text-rose-300',    label: 'GHOST' },
  }[snap.tier] || { border: 'border-border', bg: 'bg-surface/40', accent: 'text-muted-foreground', label: snap.tier || '—' };

  const orderVal = (row?.llm_extracted?.order_value_cr) ?? null;
  const daysToAbsorb = (orderVal && snap.avg_daily_turnover_cr)
    ? (orderVal / snap.avg_daily_turnover_cr)
    : null;
  const vRatio = snap.today_volume_ratio;
  const delivDrop = snap.avg_deliv_pct_30d && snap.today_deliv_pct != null
    ? (snap.today_deliv_pct - snap.avg_deliv_pct_30d)
    : null;

  const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
  const int = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));

  return (
    <div
      className={`rounded border ${tierPalette.border} ${tierPalette.bg} px-3 py-2.5 space-y-2`}
      data-testid={`lodr-liquidity-panel-${symbol}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] uppercase tracking-wider font-bold ${tierPalette.accent}`}>
          {snap.emoji || '📊'} Liquidity · {tierPalette.label}
        </span>
        {snap.tier_description && (
          <span className="text-[10px] text-zinc-500 italic">
            {snap.tier_description}
          </span>
        )}
        <span className="ml-auto text-[10px] text-zinc-500 font-mono">
          as of {snap.as_of_date} · {snap.samples_days}d sample
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">30-day avg turnover</div>
          <div className="text-foreground font-mono font-semibold">
            ₹{num(snap.avg_daily_turnover_cr, 2)} Cr/day
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">30-day avg volume</div>
          <div className="text-foreground font-mono font-semibold">
            {int(snap.avg_daily_volume)} sh
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Today&apos;s turnover</div>
          <div className="text-foreground font-mono font-semibold">
            ₹{num(snap.today_turnover_cr, 2)} Cr
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Today&apos;s volume</div>
          <div className="text-foreground font-mono font-semibold">
            {int(snap.today_volume)} sh
            {vRatio != null && (
              <span className={`ml-1 text-[10px] ${vRatio >= 3 ? 'text-orange-300' : vRatio >= 2 ? 'text-amber-300' : 'text-zinc-500'}`}>
                ({num(vRatio, 1)}× avg)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] pt-1 border-t border-border/40">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Deliv % (today)</div>
          <div className="text-foreground font-mono font-semibold">
            {num(snap.today_deliv_pct, 1)}%
            {delivDrop != null && (
              <span className={`ml-1 text-[10px] ${delivDrop <= -20 ? 'text-rose-300' : delivDrop <= -10 ? 'text-amber-300' : 'text-zinc-500'}`}>
                ({delivDrop >= 0 ? '+' : ''}{num(delivDrop, 1)} vs 30d)
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Deliv % (30-day avg)</div>
          <div className="text-foreground font-mono font-semibold">
            {num(snap.avg_deliv_pct_30d, 1)}%
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Last close</div>
          <div className="text-foreground font-mono font-semibold">
            ₹{num(snap.today_close, 2)}
            {snap.price_change_30d_pct != null && (
              <span className={`ml-1 text-[10px] ${snap.price_change_30d_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                ({snap.price_change_30d_pct >= 0 ? '+' : ''}{num(snap.price_change_30d_pct, 1)}% 30d)
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Days to absorb order</div>
          <div className="text-foreground font-mono font-semibold">
            {daysToAbsorb == null ? '—' : (
              <span className={daysToAbsorb >= 20 ? 'text-rose-300' : daysToAbsorb >= 5 ? 'text-amber-300' : 'text-emerald-300'}>
                {num(daysToAbsorb, 1)}× turnover
              </span>
            )}
          </div>
        </div>
      </div>

      {(snap.deliv_pct_collapse || (vRatio && vRatio >= 3)) && (
        <div className="text-[11px] text-amber-200 leading-snug pt-1 border-t border-amber-500/20">
          <span className="font-semibold uppercase text-[9px] tracking-wider text-amber-300">Flags:</span>{' '}
          {snap.deliv_pct_collapse && <>Delivery % collapsed — operator activity likely. </>}
          {vRatio && vRatio >= 3 && <>Today&apos;s volume is {num(vRatio, 1)}× normal — orchestrated buying. </>}
        </div>
      )}
    </div>
  );
};


const DeepAnalysisCard = ({ row, readOnly = false }) => {
  const deep = row?.deep_analysis || {};
  const p1 = deep.phase1 || null;
  const p2 = deep.phase2 || null;
  const llm = row?.llm_extracted || {};

  if (!p1 && !p2) {
    return (
      <div
        className="text-xs text-muted-foreground font-sans leading-relaxed"
        data-testid={`lodr-deep-empty-${row._id}`}
      >
        {readOnly ? (
          <>No deep analysis available yet for this row. Check back shortly.</>
        ) : (
          <>
            No deep analysis yet for this row. Phase 1 fires automatically
            for orders ≥ ₹1 Cr; Phase 2 (LLM bullishness) runs after Phase 1.
            Trigger a backfill if this row is older than the deploy.
          </>
        )}
      </div>
    );
  }

  const verdict = (p2?.verdict || '').toUpperCase();
  // 147j+16ch — Flag deterministic MINOR bundles so the expander
  // surfaces a clear banner instead of looking like missing data.
  const isDeterministicMinor = !!p2?._deterministic
    && p2?._impact_tier === 'MINOR';
  // Session 147j+16bk — Union p1 (deterministic) + p2 (LLM) flags so
  // the chip strip always shows balance-sheet red flags even when
  // Phase 2 hasn't run yet or the LLM forgot them. p2 already unions
  // these server-side, but a Phase-1-only row should still surface
  // chips so we merge defensively in the UI too.
  const p1Flags = (p1?.risk_flags) || [];
  const p2Flags = (p2?.risk_flags) || [];
  const flags = Array.from(new Set([...p2Flags, ...p1Flags]));
  const bull = (p2?.bull_case) || [];
  const bear = (p2?.bear_case) || [];

  return (
    <div
      className="space-y-4 font-sans"
      data-testid={`lodr-deep-card-${row._id}`}
    >
      {/* ── SECTION 1 — VERDICT HERO STRIP ────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-border/40">
        {verdict ? (
          <span
            className={`px-3 py-1 text-xs uppercase tracking-wider font-bold border rounded ${verdictTone[verdict] || verdictTone.SKIP}`}
            data-testid={`lodr-verdict-${verdict.toLowerCase()}`}
          >
            {verdict}
          </span>
        ) : (
          <span className="px-3 py-1 text-xs uppercase tracking-wider font-bold border rounded bg-surface-highlight text-muted-foreground">
            Verdict pending
          </span>
        )}
        {p2 && (
          <>
            <div className="flex items-center gap-3 min-w-[200px]">
              <div className="flex-1 space-y-1">
                <ScoreBar score={p2.short_term_score} label="Short" hint="Likely 1-3 day price impact" />
                <ScoreBar score={p2.long_term_score} label="Long" hint="FY27 P&L durability" />
              </div>
            </div>
            {p2.catalyst_strength && (
              <span
                className={`px-2 py-1 text-[10px] uppercase tracking-wider border rounded-sm ${catalystTone[p2.catalyst_strength] || catalystTone.MAINTENANCE}`}
              >
                {p2.catalyst_strength}
              </span>
            )}
            {p2.sector_tailwind && (
              <span
                className={`text-[10px] uppercase tracking-wider ${sectorTone[p2.sector_tailwind] || sectorTone.NEUTRAL}`}
                title="Sector tailwind"
              >
                Sector: {p2.sector_tailwind}
              </span>
            )}
            {p2.margin_impact && (
              <span
                className={`text-[10px] uppercase tracking-wider ${marginTone[p2.margin_impact] || marginTone.NEUTRAL}`}
                title="Expected margin impact"
              >
                Margin: {p2.margin_impact}
              </span>
            )}
            {p2.earnings_visibility_months != null && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                P&amp;L in ~{p2.earnings_visibility_months} mo
              </span>
            )}
            {p2.confidence != null && (() => {
              // 147j+16db — colour-code the CONF chip so the user
              // knows at a glance whether to trust the verdict.
              //   ≥ 90%  → high confidence (emerald)
              //   80-89% → solid (zinc)
              //   75-79% → borderline (amber)
              //   < 75%  → hedging (rose)
              const pct = Math.round(p2.confidence * 100);
              const tone =
                pct >= 90 ? 'text-emerald-300'
                : pct >= 80 ? 'text-zinc-300'
                : pct >= 75 ? 'text-amber-300'
                : 'text-rose-300';
              return (
                <span
                  className={`text-[10px] uppercase tracking-wider ml-auto font-semibold ${tone} cursor-help`}
                  title={
                    'AI verdict confidence — how sure the model is about the ' +
                    'BUY/HOLD/SKIP call it just made.\n\n' +
                    'Benchmark:\n' +
                    '  ≥ 90%  → HIGH — trust the call as-is\n' +
                    '  80-89% → SOLID — no action needed\n' +
                    '  75-79% → BORDERLINE — glance at Deep Analysis\n' +
                    '  < 75%  → HEDGING — read the filing yourself\n\n' +
                    'Anything below 80% also raises a "Low confidence" flag ' +
                    'in the row below.'
                  }
                  data-testid={`lodr-conf-chip-${row._id}`}
                >
                  Conf {pct}%
                </span>
              );
            })()}
          </>
        )}
      </div>

      {/* 147j+16ch — Deterministic Minor banner. Tells the user
          why the expander looks sparse: no LLM ran for this row's
          verdict. Saves credits, but the UI must explain it. */}
      {isDeterministicMinor && (
        <div
          className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200 leading-snug"
          data-testid={`lodr-minor-banner-${row._id}`}
        >
          <strong className="font-semibold">LOW IMPACT — full AI verdict skipped.</strong>
          {' '}This order didn't clear the Major threshold (≥ 5% of TTM revenue,
          ≥ 2% of market cap, or a Tier-1 customer). The verdict, thesis, and
          bull/bear cases below are <em>deterministic placeholders</em> — no
          AI call ran for this row.
          {readOnly ? (
            <> Treat it as order-book accretion, not a re-rating catalyst.</>
          ) : (
            <> If you want the full analysis anyway, use the{' '}
              <strong>🔧 wrench → Force re-extract</strong> button on the row
              (~0.3 credits).
            </>
          )}
        </div>
      )}

      {p2?.verdict_reason && (
        <div
          className="text-[13px] font-semibold text-foreground leading-snug"
          data-testid={`lodr-verdict-reason-${row._id}`}
        >
          {p2.verdict_reason}
        </div>
      )}

      {/* 147j+16cs — Red-Flag Inspector section. Two-layer "fishy order"
          detection that sits ON TOP of the bullishness verdict.
          Layer A = deterministic flags (free). Layer B = Gemini
          inspector (only on BUY + big rows). Rendered as a prominent
          card so a trader cannot miss it while reading the BUY.
          147j+16cz — When the ONLY flag is LOW_LLM_CONF (a data-quality
          hedge, not a red flag about the deal), suppress the big rose
          card and render a subtle inline warning instead so it doesn't
          look scarier than it actually is. */}
      {(() => {
        const rfList = p2?.red_flags || [];
        const hasInspector = !!p2?.red_flag_inspector;
        const onlyLowConf =
          !hasInspector &&
          rfList.length === 1 &&
          (rfList[0]?.code === 'LOW_LLM_CONF');
        if (onlyLowConf) {
          const f = rfList[0];
          const benchmarkTip =
            (f.detail || '') +
            '\n\nConfidence benchmark:\n' +
            '  ≥ 90%  → HIGH — trust the call as-is\n' +
            '  80-89% → SOLID — no action needed\n' +
            '  75-79% → BORDERLINE — glance at Deep Analysis\n' +
            '  < 75%  → HEDGING — read the filing yourself';
          return (
            <div
              className="text-[11px] text-zinc-400 flex items-center gap-1.5 italic cursor-help"
              title={benchmarkTip}
              data-testid={`lodr-lowconf-inline-${row._id}`}
            >
              <span className="text-amber-400/80">ℹ</span>
              <span>
                <span className="text-amber-300/90 font-medium not-italic">
                  {f.label}
                </span>{' '}
                — AI is unsure of its own extraction. Sanity-check numbers
                against the filing.{' '}
                <span className="text-zinc-500 not-italic">
                  (hover for scale)
                </span>
              </span>
            </div>
          );
        }
        return null;
      })()}
      {(() => {
        const rfList = p2?.red_flags || [];
        const hasInspector = !!p2?.red_flag_inspector;
        const onlyLowConf =
          !hasInspector &&
          rfList.length === 1 &&
          (rfList[0]?.code === 'LOW_LLM_CONF');
        const shouldRenderCard =
          !onlyLowConf && (rfList.length > 0 || hasInspector);
        if (!shouldRenderCard) return null;
        return (
        (() => {
          // Session 147j+17c — Tier-based visual palette. A 0/10 score
          // is the CLEANEST possible outcome (calibration: 0=clean firm
          // order, 4-6=hedges/execution risk, 7-10=self-dealing). The
          // old always-rose card made clean rows look scary. Now:
          //   0-3 → emerald ✓  (Inspector: clean)
          //   4-6 → amber  ⚠   (Inspector: caution)
          //   7-10 → rose  🚨  (Inspector: high risk)
          //
          // If Layer B (LLM) didn't run but Layer A has red_flags,
          // fall back to rose (deterministic flags are always warnings).
          const score = p2.red_flag_inspector?.red_flag_score;
          const hasScore = score != null;
          let tier = 'rose';
          if (hasScore) {
            if (score <= 3) tier = 'emerald';
            else if (score <= 6) tier = 'amber';
            else tier = 'rose';
          }
          const palette = {
            emerald: {
              border: 'border-emerald-500/40',
              bg: 'bg-emerald-500/5',
              accent: 'text-emerald-300',
              summary: 'text-emerald-100',
              scoreLine: 'text-emerald-200',
              scoreVal: 'text-emerald-100',
              icon: '✓',
              label: 'Red-Flag Inspector · clean',
            },
            amber: {
              border: 'border-amber-500/40',
              bg: 'bg-amber-500/5',
              accent: 'text-amber-300',
              summary: 'text-amber-100',
              scoreLine: 'text-amber-200',
              scoreVal: 'text-amber-100',
              icon: '⚠',
              label: 'Red-Flag Inspector · caution',
            },
            rose: {
              border: 'border-rose-500/40',
              bg: 'bg-rose-500/5',
              accent: 'text-rose-300',
              summary: 'text-rose-100',
              scoreLine: 'text-rose-200',
              scoreVal: 'text-rose-100',
              icon: hasScore ? '🚨' : '⚠',
              label: hasScore ? 'Red-Flag Inspector · high risk' : 'Red-Flag Inspector',
            },
          }[tier];
          return (
        <div
          className={`rounded border ${palette.border} ${palette.bg} px-3 py-2.5 space-y-2`}
          data-testid={`lodr-redflag-card-${row._id}`}
          data-tier={tier}
        >
          <div className="flex items-baseline gap-2">
            <span className={`text-[11px] uppercase tracking-wider font-bold ${palette.accent}`}>
              {palette.icon} {palette.label}
            </span>
            {hasScore && (
              <span className={`text-[11px] font-mono ${palette.scoreLine}`}>
                Score{' '}
                <strong className={palette.scoreVal}>
                  {score}/10
                </strong>
              </span>
            )}
            <span className="ml-auto text-[10px] text-zinc-500 font-mono">
              {(p2.red_flags || []).length} det
              {p2.red_flag_inspector ? ' · LLM inspected' : ''}
            </span>
          </div>

          {p2.red_flag_inspector?.one_line_summary && (
            <div className={`text-[12px] ${palette.summary} leading-snug font-medium`}>
              {p2.red_flag_inspector.one_line_summary}
            </div>
          )}

          {/* Layer A — deterministic chips */}
          {(p2.red_flags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {(p2.red_flags || []).map((f, i) => (
                <span
                  key={i}
                  title={f.detail}
                  className={
                    'px-1.5 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm cursor-help ' +
                    (f.severity === 'high'
                      ? 'border-rose-500/60 bg-rose-500/15 text-rose-200'
                      : f.severity === 'warn'
                        ? 'border-amber-500/60 bg-amber-500/15 text-amber-200'
                        : 'border-sky-500/60 bg-sky-500/15 text-sky-200')
                  }
                  data-testid={`lodr-redflag-chip-${row._id}-${f.code.toLowerCase()}`}
                >
                  {f.label}
                </span>
              ))}
            </div>
          )}

          {/* Layer B — Gemini inspector breakdown */}
          {p2.red_flag_inspector && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pt-1 text-[11px] text-zinc-300 leading-snug">
              {p2.red_flag_inspector.related_party && (
                <div>
                  <span className="text-rose-300 font-semibold uppercase text-[9px] tracking-wider block">
                    Related party
                  </span>
                  <span>{p2.red_flag_inspector.related_party}</span>
                </div>
              )}
              {p2.red_flag_inspector.replaces_cancelled_contract && (
                <div>
                  <span className="text-rose-300 font-semibold uppercase text-[9px] tracking-wider block">
                    Replaces cancelled
                  </span>
                  <span>{p2.red_flag_inspector.replaces_cancelled_contract}</span>
                </div>
              )}
              {p2.red_flag_inspector.corporate_restructuring && (
                <div>
                  <span className="text-amber-300 font-semibold uppercase text-[9px] tracking-wider block">
                    Restructuring
                  </span>
                  <span>{p2.red_flag_inspector.corporate_restructuring}</span>
                </div>
              )}
              {p2.red_flag_inspector.circular_revenue_risk && (
                <div>
                  <span className="text-amber-300 font-semibold uppercase text-[9px] tracking-wider block">
                    Circular revenue
                  </span>
                  <span>{p2.red_flag_inspector.circular_revenue_risk}</span>
                </div>
              )}
              {(p2.red_flag_inspector.language_hedges || []).length > 0 && (
                <div className="md:col-span-2">
                  <span className="text-sky-300 font-semibold uppercase text-[9px] tracking-wider block">
                    Language hedges
                  </span>
                  <span>
                    {(p2.red_flag_inspector.language_hedges || []).join(' · ')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
          );
        })()
        );
      })()}

      {/* Session 147j+17c — Liquidity snapshot. Answers "can I actually
          trade this at size?" before acting on the BUY verdict. */}
      {row?.nse_symbol && (
        <LiquidityPanel symbol={row.nse_symbol} row={row} />
      )}

      {/* 147j+16cw — Deep Analysis Report (BHEL-vs-Bluspring style).
          Only renders when the LLM/bundle produced meaningful data.
          Layout mirrors the paid ChatGPT report: Snapshot → Ratios
          → Quality → Sector benchmark. */}
      {(p2?.execution_period_months != null
        || p2?.order_type_detail
        || p2?.margin_profile
        || p2?.annualized_revenue_uplift_pct != null
        || (p2?.deep_analysis_bundle || {}).book_to_bill) && (
        <div
          className="rounded border border-sky-500/40 bg-sky-500/5 px-3 py-2.5 space-y-2.5"
          data-testid={`lodr-deep-analysis-${row._id}`}
        >
          <div className="text-[11px] uppercase tracking-wider font-bold text-sky-300">
            📊 Deep Analysis Report
          </div>

          {/* Row 1 — Snapshot chips */}
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            {p2.order_type_detail && (
              <span className="px-1.5 py-0.5 border border-sky-500/60 bg-sky-500/10 text-sky-200 uppercase rounded font-semibold">
                {p2.order_type_detail}
              </span>
            )}
            {p2.execution_period_months != null && (
              <span className="px-1.5 py-0.5 border border-zinc-600 bg-zinc-800/60 text-zinc-200 rounded font-mono">
                Exec: {p2.execution_period_months} mo
              </span>
            )}
            {p2.margin_profile && p2.margin_profile !== 'UNKNOWN' && (
              <span className={`px-1.5 py-0.5 border rounded font-semibold uppercase ${
                p2.margin_profile === 'HIGH'
                  ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200'
                  : p2.margin_profile === 'LOW'
                    ? 'border-rose-500/60 bg-rose-500/10 text-rose-200'
                    : 'border-amber-500/60 bg-amber-500/10 text-amber-200'
              }`}>
                Margin: {p2.margin_profile}
              </span>
            )}
            {p2.working_capital_impact && p2.working_capital_impact !== 'UNKNOWN' && (
              <span className={`px-1.5 py-0.5 border rounded font-semibold uppercase ${
                p2.working_capital_impact === 'POSITIVE'
                  ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200'
                  : p2.working_capital_impact === 'NEGATIVE'
                    ? 'border-rose-500/60 bg-rose-500/10 text-rose-200'
                    : 'border-zinc-600 bg-zinc-800/60 text-zinc-200'
              }`}>
                WC: {p2.working_capital_impact}
              </span>
            )}
          </div>

          {/* Row 2 — Ratio grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            {p2.annualized_revenue_uplift_pct != null && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-sky-300 font-semibold">
                  Annualised uplift
                </div>
                <div className="font-mono text-foreground">
                  {p2.annualized_revenue_uplift_pct.toFixed(1)}%
                  <span className="text-zinc-500"> / yr</span>
                </div>
              </div>
            )}
            {(p2?.deep_analysis_bundle?.book_to_bill?.book_to_bill != null) && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-sky-300 font-semibold">
                  Book-to-Bill (90d)
                </div>
                <div className="font-mono text-foreground">
                  {p2.deep_analysis_bundle.book_to_bill.book_to_bill.toFixed(2)}×
                  <span className="text-zinc-500">
                    {' '}({p2.deep_analysis_bundle.book_to_bill.orders_in_window} orders)
                  </span>
                </div>
              </div>
            )}
            {row.value_pct_of_ttm != null && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-sky-300 font-semibold">
                  vs TTM revenue
                </div>
                <div className="font-mono text-foreground">
                  {row.value_pct_of_ttm.toFixed(1)}%
                </div>
              </div>
            )}
            {row.value_pct_of_mcap != null && (
              <div>
                <div className="text-[9px] uppercase tracking-wider text-sky-300 font-semibold">
                  vs Market cap
                </div>
                <div className="font-mono text-foreground">
                  {row.value_pct_of_mcap.toFixed(1)}%
                </div>
              </div>
            )}
          </div>

          {/* Row 3 — Sector benchmark verdict */}
          {p2?.deep_analysis_bundle?.sector_benchmark && (
            <div className="pt-1 border-t border-sky-500/20">
              <div className="flex items-baseline gap-2">
                <span className="text-[9px] uppercase tracking-wider text-sky-300 font-semibold">
                  Sector benchmark
                </span>
                <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border rounded ${
                  p2.deep_analysis_bundle.sector_benchmark.verdict === 'OUTLIER'
                    ? 'border-rose-500/60 bg-rose-500/10 text-rose-200'
                    : p2.deep_analysis_bundle.sector_benchmark.verdict === 'BELOW'
                      ? 'border-amber-500/60 bg-amber-500/10 text-amber-200'
                      : p2.deep_analysis_bundle.sector_benchmark.verdict === 'WITHIN'
                        ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200'
                        : 'border-zinc-600 bg-zinc-800/60 text-zinc-300'
                }`}>
                  {p2.deep_analysis_bundle.sector_benchmark.verdict}
                </span>
                <span className="text-[10px] text-zinc-400 font-mono">
                  healthy {p2.deep_analysis_bundle.sector_benchmark.healthy_low}×
                  -{p2.deep_analysis_bundle.sector_benchmark.healthy_high}×
                </span>
              </div>
              <div className="text-[11px] text-zinc-300 leading-snug mt-0.5">
                {p2.deep_analysis_bundle.sector_benchmark.note}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 147j+16bi — Dual-mode verdict (Trader vs Investor) strip.
          Lets the user see how the SAME order looks under a news-
          catalyst lens vs an execution-durability lens. The two can
          legitimately disagree on rows like COCKERILL (JSW LOI) where
          trader=BUY but investor=WATCH. Render only when at least one
          lens differs from the primary verdict OR has its own reason. */}
      {p2 && (p2.trader_verdict || p2.investor_verdict) && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1"
          data-testid={`lodr-dual-mode-${row._id}`}
        >
          {p2.trader_verdict && (
            <div className="border rounded-md p-2.5 bg-surface-highlight/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                  Trader · 1-5 day
                </span>
                <span
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border rounded ${verdictTone[p2.trader_verdict.toUpperCase()] || verdictTone.SKIP}`}
                  data-testid={`lodr-trader-verdict-${row._id}`}
                >
                  {p2.trader_verdict}
                  {p2.trader_confidence != null && (
                    <span className="ml-1 opacity-70">
                      {(p2.trader_confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </span>
              </div>
              {p2.trader_reason && (
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {p2.trader_reason}
                </p>
              )}
            </div>
          )}
          {p2.investor_verdict && (
            <div className="border rounded-md p-2.5 bg-surface-highlight/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                  Investor · 12-36 mo
                </span>
                <span
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border rounded ${verdictTone[p2.investor_verdict.toUpperCase()] || verdictTone.SKIP}`}
                  data-testid={`lodr-investor-verdict-${row._id}`}
                >
                  {p2.investor_verdict}
                  {p2.investor_confidence != null && (
                    <span className="ml-1 opacity-70">
                      {(p2.investor_confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </span>
              </div>
              {p2.investor_reason && (
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {p2.investor_reason}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── SECTION 2 — THESIS + BULL/BEAR ─────────────────────────── */}
      {p2 ? (
        <div className="space-y-4">
          {/* 147j+16aw — Order velocity (momentum) snapshot. Surfaces
              hot streaks (3rd order in 30d ≠ 1st order) so the user
              can spot momentum at a glance even before reading the
              thesis. */}
          <MomentumSection phase1={p1} rowId={row._id} />

          {/* 147j+16bp — Smart Money Activity (bulk + block deals
              from notable HNIs / MFs / FIIs around the announcement
              date). Renders only when matched deals exist. */}
          <SmartMoneySection row={row} />

          {/* Thesis paragraph — uses full container width so it never
              gets clipped by a max-w constraint. */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Thesis
            </div>
            <p
              className="text-[13px] text-foreground/90 leading-relaxed whitespace-normal break-words"
              data-testid={`lodr-thesis-${row._id}`}
            >
              {p2.thesis || p2.rationale_short || 'No thesis generated.'}
            </p>
          </div>

          {/* Bull / Bear — always 2 columns side-by-side (collapses
              to 1 col on mobile). Key question + risk flags now live
              in their own full-width row beneath so nothing gets
              clipped on horizontally-scrolled tables. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bull case */}
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">
                Bull case
              </div>
              {bull.length > 0 ? (
                <ul className="space-y-1 text-[12px] text-foreground/85">
                  {bull.map((b, i) => (
                    <li key={i} className="flex gap-2 min-w-0" data-testid={`lodr-bull-case-${row._id}-${i}`}>
                      <span className="text-emerald-400 shrink-0">▲</span>
                      <span className="whitespace-normal break-words min-w-0 flex-1">{b}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-[11px] text-muted-foreground italic">No bull-case bullets.</div>
              )}
            </div>

            {/* Bear case */}
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-rose-300 font-bold">
                Bear case
              </div>
              {bear.length > 0 ? (
                <ul className="space-y-1 text-[12px] text-foreground/85">
                  {bear.map((b, i) => (
                    <li key={i} className="flex gap-2 min-w-0" data-testid={`lodr-bear-case-${row._id}-${i}`}>
                      <span className="text-rose-400 shrink-0">▼</span>
                      <span className="whitespace-normal break-words min-w-0 flex-1">{b}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-[11px] text-muted-foreground italic">No bear-case bullets.</div>
              )}
            </div>
          </div>

          {/* Key question + risk flags — full-width row below
              bull/bear so the question text never gets clipped. */}
          {(p2.key_question || flags.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              {p2.key_question && (
                <div
                  className="md:col-span-2 px-3 py-2 border-l-2 border-amber-400 bg-amber-500/5 text-[12px] text-amber-100 italic leading-snug whitespace-normal break-words"
                  data-testid={`lodr-key-question-${row._id}`}
                >
                  <span className="text-[10px] uppercase tracking-wider text-amber-300 font-bold not-italic block mb-0.5">
                    Key question to verify
                  </span>
                  {p2.key_question}
                </div>
              )}
              {flags.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Risk flags
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {flags.map((f) => {
                      const tone = RISK_FLAG_TONE[f] || 'rose';
                      const cls = RISK_FLAG_CHIP_CLASSES[tone] || RISK_FLAG_CHIP_CLASSES.rose;
                      return (
                        <span
                          key={f}
                          className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider border rounded-sm whitespace-nowrap ${cls}`}
                          data-testid={`lodr-risk-flag-${f}`}
                        >
                          {f.replace(/_/g, ' ')}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-[11px] text-muted-foreground italic">
            {readOnly ? (
              <>Bullishness not yet computed for this row. The next refresh will populate it.</>
            ) : (
              <>
                Phase 2 (LLM bullishness) not yet computed for this row. Trigger a
                backfill via <code className="font-mono text-foreground/80">POST /api/admin/lodr/deep-analysis/phase2/backfill</code>.
              </>
            )}
          </div>
          {/* 147j+16bk — Surface Phase 1 deterministic flags even when
              Phase 2 hasn't run yet. Balance-sheet red flags are a
              first-class signal and should never wait on the LLM. */}
          {flags.length > 0 && (
            <div data-testid={`lodr-phase1-only-flags-${row._id}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                Risk flags
              </div>
              <div className="flex gap-1 flex-wrap">
                {flags.map((f) => {
                  const tone = RISK_FLAG_TONE[f] || 'rose';
                  const cls = RISK_FLAG_CHIP_CLASSES[tone] || RISK_FLAG_CHIP_CLASSES.rose;
                  return (
                    <span
                      key={f}
                      className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider border rounded-sm whitespace-nowrap ${cls}`}
                      data-testid={`lodr-risk-flag-${f}`}
                    >
                      {f.replace(/_/g, ' ')}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SECTION 3 — DETERMINISTIC CONTEXT ──────────────────────── */}
      <div className="pt-3 border-t border-border/40">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
          Deterministic context
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-1.5 text-[11px] font-mono">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Size</span>
            <span className="text-foreground uppercase">{p1?.size_tier || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">MCap</span>
            <span className="text-foreground">
              {p1?.market_cap_cr ? `₹${Number(p1.market_cap_cr).toFixed(0)} Cr` : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">% MCap</span>
            <span className="text-foreground">
              {p1?.value_pct_of_mcap != null
                ? `${Number(p1.value_pct_of_mcap).toFixed(2)}%`
                : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">% TTM</span>
            <span className="text-foreground">
              {row.value_pct_of_ttm != null
                ? `${Number(row.value_pct_of_ttm).toFixed(2)}%`
                : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Repeat</span>
            <span className={p1?.is_repeat_customer ? 'text-emerald-300' : 'text-muted-foreground'}>
              {p1?.is_repeat_customer ? 'YES' : 'no'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Tenure</span>
            <span className="text-foreground">
              {llm.completion_months ? `${llm.completion_months} mo` : '—'}
            </span>
          </div>
        </div>
        {/* Session 147j+16bj — Fundamentals strip (Investor lens context).
            Renders only when Phase 1 produced a quality_grade — older
            rows without the fundamentals snapshot fall through with no
            UI change. */}
        {p1?.quality_grade && (
          <div
            className="mt-2 pt-2 border-t border-border/30 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono"
            data-testid={`lodr-quality-strip-${row._id}`}
          >
            <span className="text-muted-foreground">Quality</span>
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                p1.quality_grade === 'A'
                  ? 'border-emerald-400/60 text-emerald-200 bg-emerald-500/10'
                  : p1.quality_grade === 'B'
                  ? 'border-sky-400/60 text-sky-200 bg-sky-500/10'
                  : p1.quality_grade === 'C'
                  ? 'border-amber-400/60 text-amber-200 bg-amber-500/10'
                  : 'border-rose-400/60 text-rose-200 bg-rose-500/10'
              }`}
              data-testid={`lodr-quality-grade-${row._id}`}
            >
              {p1.quality_grade}
            </span>
            {p1.fundamentals?.d_e != null && (
              <span className="text-foreground">
                <span className="text-muted-foreground">D/E </span>
                {Number(p1.fundamentals.d_e).toFixed(2)}
              </span>
            )}
            {p1.fundamentals?.interest_coverage != null && (
              <span className="text-foreground">
                <span className="text-muted-foreground">ICR </span>
                {Number(p1.fundamentals.interest_coverage).toFixed(1)}x
              </span>
            )}
            {p1.fundamentals?.promoter_pct != null && (
              <span className="text-foreground">
                <span className="text-muted-foreground">Prom </span>
                {Number(p1.fundamentals.promoter_pct).toFixed(0)}%
                {p1.fundamentals.promoter_delta_4q != null && p1.fundamentals.promoter_delta_4q !== 0 && (
                  <span
                    className={
                      p1.fundamentals.promoter_delta_4q > 0
                        ? 'text-emerald-300 ml-0.5'
                        : 'text-rose-300 ml-0.5'
                    }
                  >
                    {p1.fundamentals.promoter_delta_4q > 0 ? '↑' : '↓'}
                    {Math.abs(p1.fundamentals.promoter_delta_4q).toFixed(1)}%
                  </span>
                )}
              </span>
            )}
            {p1.fundamentals?.pledge_pct != null && p1.fundamentals.pledge_pct > 0 && (
              <span className="text-rose-300">
                <span className="text-muted-foreground">Pledge </span>
                {Number(p1.fundamentals.pledge_pct).toFixed(0)}%
              </span>
            )}
            {p1.fundamentals?.ocf_pat_5y != null && (
              <span className="text-foreground">
                <span className="text-muted-foreground">OCF/PAT </span>
                {Number(p1.fundamentals.ocf_pat_5y).toFixed(2)}
              </span>
            )}
            {p1?.roce_pct != null && (
              <span className="text-foreground">
                <span className="text-muted-foreground">ROCE </span>
                {Number(p1.roce_pct).toFixed(1)}%
              </span>
            )}
            {p1.fundamentals?.fundamentals_partial && (
              <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 italic">
                · partial
              </span>
            )}
          </div>
        )}
        {/* Session 147j+17o — MCap Impact projection. Deterministic
            math (no LLM cost). Renders only when we have net_margin
            + P/E TTM. Same block the LLM sees, exposed to admins so
            they can eyeball the "why BUY" arithmetic instantly. */}
        {p1?.mcap_impact && (
          <div
            className="mt-3 rounded border border-emerald-500/25 bg-emerald-500/5 px-3 py-2.5"
            data-testid={`lodr-mcap-impact-${row._id}`}
          >
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-[11px] uppercase tracking-wider font-bold text-emerald-300">
                📈 MCap Impact (projected)
              </span>
              <span className="text-[10px] text-muted-foreground italic">
                deterministic · not LLM
              </span>
              <span
                className={`ml-auto text-[11px] font-mono font-semibold ${
                  (p1.mcap_impact.upside_pct || 0) >= 20
                    ? 'text-emerald-300'
                    : (p1.mcap_impact.upside_pct || 0) >= 5
                    ? 'text-lime-300'
                    : 'text-muted-foreground'
                }`}
              >
                {p1.mcap_impact.upside_pct != null
                  ? `${p1.mcap_impact.upside_pct >= 0 ? '+' : ''}${Number(p1.mcap_impact.upside_pct).toFixed(2)}% upside`
                  : '—'}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Incremental PAT</div>
                <div className="font-mono font-semibold text-foreground">
                  ₹{Number(p1.mcap_impact.pat_incremental_cr).toFixed(2)} Cr
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">MCap Expansion</div>
                <div className="font-mono font-semibold text-foreground">
                  ₹{Number(p1.mcap_impact.mcap_expansion_cr).toFixed(2)} Cr
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Current MCap</div>
                <div className="font-mono font-semibold text-foreground">
                  ₹{Number(p1.mcap_impact.inputs?.current_mcap_cr).toFixed(0)} Cr
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Target MCap</div>
                <div className="font-mono font-semibold text-emerald-200">
                  ₹{Number(p1.mcap_impact.target_mcap_cr).toFixed(0)} Cr
                </div>
              </div>
            </div>
            <div className="mt-1.5 text-[10px] text-muted-foreground font-mono">
              Inputs: order ₹{Number(p1.mcap_impact.inputs?.order_value_cr).toFixed(2)} Cr
              × net margin {Number(
                p1.mcap_impact.inputs?.net_margin_pct_used ?? p1.mcap_impact.inputs?.net_margin_pct
              ).toFixed(1)}%
              {p1.mcap_impact.margin_capped && (
                <span
                  className="ml-1 text-amber-300"
                  title={`Actual TTM margin ${Number(p1.mcap_impact.inputs?.net_margin_pct).toFixed(1)}% capped at ${Number(p1.mcap_impact.inputs?.margin_cap_pct ?? 25).toFixed(0)}% for projection`}
                >
                  (capped from {Number(p1.mcap_impact.inputs?.net_margin_pct).toFixed(1)}%)
                </span>
              )}
              × P/E {Number(p1.mcap_impact.inputs?.pe_ttm).toFixed(1)}
            </div>
            {p1.mcap_impact.high_margin_warning && (
              <div
                className="mt-2 flex items-start gap-1.5 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-2 py-1"
                data-testid="mcap-impact-high-margin-warning"
              >
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-none" />
                <span>
                  High-margin ({Number(p1.mcap_impact.inputs?.net_margin_pct).toFixed(1)}%)
                  — verify TTM. Uncapped margins often include one-off gains
                  (asset sales, tax refunds) or a single large order lifting TTM.
                  Projection above uses a capped {Number(p1.mcap_impact.inputs?.margin_cap_pct ?? 25).toFixed(0)}%
                  sustainable margin instead.
                </span>
              </div>
            )}
          </div>
        )}
        {/* Session 2026-02 — Diagnostic banner when the projection was
            dropped due to a missing fundamentals input. Renders only
            when we had the base inputs (order value + mcap) but
            fundamentals (margin / PE) were missing or out of range. */}
        {!p1?.mcap_impact && p1?.mcap_impact_skip_reason && (
          <div
            className="mt-3 rounded border border-zinc-500/25 bg-zinc-500/5 px-3 py-2 flex items-start gap-1.5 text-[10px] text-muted-foreground"
            data-testid={`lodr-mcap-impact-skip-${row._id}`}
          >
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-none text-amber-400" />
            <div>
              <span className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">
                MCap Impact unavailable
              </span>
              <span className="ml-2 font-mono">
                {p1.mcap_impact_skip_reason.split(",").map((r) => {
                  const label = {
                    missing_net_margin: "margin data missing (screener + yfinance both blank)",
                    negative_net_margin: "net margin ≤ 0 (loss-making TTM)",
                    missing_pe: "P/E missing",
                    negative_pe: "P/E ≤ 0",
                    pe_out_of_range: "P/E ≥ 500 (excluded as noisy)",
                    unknown: "unknown gating input",
                  }[r.trim()] || r.trim();
                  return label;
                }).join(" · ")}
              </span>
            </div>
          </div>
        )}
        {llm.summary && (
          <div className="mt-3 text-[11px] text-muted-foreground italic font-sans leading-snug pl-3 border-l-2 border-border/40">
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground not-italic block mb-0.5">
              Extraction summary
            </span>
            &ldquo;{llm.summary}&rdquo;
          </div>
        )}
      </div>
    </div>
  );
};



// ==================================================================
// Stats cards
// ==================================================================
const StatsBar = ({ stats }) => {
  if (!stats) return null;
  const state = stats.scan_state || {};
  const isRunning = !!state.is_running;
  const finished = state.finished_at;
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      data-testid="lodr-stats-bar"
    >
      <div className="border border-border bg-surface p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          Last {stats.days}d
        </div>
        <div className="text-2xl font-heading font-bold text-foreground mt-1">
          {stats.total_announcements}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">orders received</div>
      </div>
      <div className="border border-border bg-surface p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          Value extracted
        </div>
        <div className="text-2xl font-heading font-bold text-emerald-300 mt-1">
          {stats.with_extracted_value}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          ({stats.extraction_rate_pct}% of total)
        </div>
      </div>
      <div className="border border-border bg-surface p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          Scan state
        </div>
        <div className="text-sm font-mono text-foreground mt-1 flex items-center gap-1.5">
          {isRunning ? (
            <>
              <Activity className="w-3.5 h-3.5 text-violet-400 animate-pulse" />
              Running
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              Idle
            </>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {finished ? `last: ${fmtDate(finished)}` : 'never'}
        </div>
      </div>
      <div className="border border-border bg-surface p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          Top recipient
        </div>
        {stats.top_recipients?.[0] ? (
          <>
            <div className="text-sm font-mono font-bold text-foreground mt-1">
              {stats.top_recipients[0].symbol}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {fmtCr(stats.top_recipients[0].total_value_cr)} · {stats.top_recipients[0].count} orders
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground mt-1">—</div>
        )}
      </div>
    </div>
  );
};


// ==================================================================
// Session 147j+16ab — Intraday cron heartbeat
// Shows when the every-30-min LODR scan last ran + next expected slot.
// Powered by `corp_orders` heartbeat persisted by `run_lodr_scan`.
// ==================================================================
const IntradayCronPanel = ({ stats }) => {
  const hb = stats?.heartbeat || {};
  const lastStarted = hb.last_started_at;
  const lastFinished = hb.last_finished_at;
  const lastStatus = (hb.last_status || '').toLowerCase();
  const isHealthy = lastStatus === 'ok';
  const lastFetched = hb.last_fetched;
  const lastNew = hb.last_stored_new;
  const lastDuration = hb.last_duration_s;

  // Compute next-expected = last_started + 30 min (snap to future).
  const nextExpected = React.useMemo(() => {
    if (!lastStarted) return null;
    try {
      const t = new Date(lastStarted).getTime();
      if (!Number.isFinite(t)) return null;
      const now = Date.now();
      let cand = t + 30 * 60 * 1000;
      while (cand < now) cand += 30 * 60 * 1000;
      return new Date(cand).toISOString();
    } catch { return null; }
  }, [lastStarted]);

  const minutesAgo = React.useMemo(() => {
    if (!lastFinished) return null;
    const t = new Date(lastFinished).getTime();
    if (!Number.isFinite(t)) return null;
    return Math.round((Date.now() - t) / 60000);
  }, [lastFinished]);

  // Visual freshness: >35 min = stale (next cron missed); >60 min = bad
  const freshnessTone =
    minutesAgo == null ? 'text-muted-foreground'
    : minutesAgo > 60 ? 'text-red-300'
    : minutesAgo > 35 ? 'text-amber-300'
    : 'text-emerald-300';

  return (
    <div
      className="border border-violet-500/30 bg-violet-500/[0.04] p-4"
      data-testid="lodr-intraday-cron-panel"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity className={`w-4 h-4 ${isHealthy ? 'text-emerald-400' : 'text-amber-400'}`} />
          <span className="text-[11px] font-mono uppercase tracking-wider text-violet-200">
            Intraday LODR Cron · Every 30 min · All 7 days
          </span>
        </div>
        <span
          className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${
            isHealthy
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
              : 'border-amber-500/40 bg-amber-500/10 text-amber-300'
          }`}
          data-testid="lodr-intraday-status"
        >
          {hb.last_status || 'never'}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Last run
          </div>
          <div className="text-sm font-mono text-foreground mt-1" data-testid="lodr-intraday-last-finished">
            {lastFinished ? fmtDate(lastFinished) : '—'}
          </div>
          <div className={`text-[10px] mt-0.5 ${freshnessTone}`} data-testid="lodr-intraday-mins-ago">
            {minutesAgo != null ? `${minutesAgo} min ago` : 'no heartbeat yet'}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Next expected
          </div>
          <div className="text-sm font-mono text-foreground mt-1" data-testid="lodr-intraday-next-expected">
            {nextExpected ? fmtDate(nextExpected) : '—'}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            +30 min cadence
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Last fetched
          </div>
          <div className="text-sm font-mono text-foreground mt-1" data-testid="lodr-intraday-fetched">
            {lastFetched != null ? lastFetched : '—'} rows
          </div>
          <div className="text-[10px] text-emerald-300 mt-0.5">
            {lastNew != null ? `${lastNew} new` : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Duration
          </div>
          <div className="text-sm font-mono text-foreground mt-1" data-testid="lodr-intraday-duration">
            {lastDuration != null ? `${lastDuration}s` : '—'}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            backend wall time
          </div>
        </div>
      </div>
      {hb.last_error && (
        <div
          className="mt-3 text-[11px] font-mono text-red-300 border border-red-500/30 bg-red-500/10 px-2 py-1"
          data-testid="lodr-intraday-error"
        >
          {hb.last_error}
        </div>
      )}
    </div>
  );
};


// ==================================================================
// Daily Pipeline status card (Session 147j+10)
// ==================================================================
const fmtIso = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const DailyPipelinePanel = ({ state, onRunNow }) => {
  const running = state?.is_running;
  const status = state?.last_run_status;
  const statusTone = running ? 'text-cyan-300'
    : status === 'ok' ? 'text-emerald-300'
      : status === 'partial' ? 'text-amber-300'
        : status === 'error' ? 'text-rose-300'
          : 'text-muted-foreground';
  const profiles = state?.last_run_summary?.steps?.profiles || {};
  return (
    <div className="border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="lodr-scheduler-panel">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Daily Pipeline · {state?.schedule_time_ist || '07:30'} IST · {state?.schedule_days || 'Mon-Fri'}
          </div>
          <div className="text-[10px] text-muted-foreground font-mono mt-1 max-w-2xl">
            Chains: LODR scan → data fetch → scoring (6-factor w/ order-flow)
            → rebalance picks across Largecap, Hunter, ValueHunter.
          </div>
        </div>
        <button
          data-testid="lodr-pipeline-run-now-btn"
          onClick={onRunNow}
          disabled={running}
          className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {running ? 'Running…' : 'Run now'}
        </button>
      </div>
      <div className="mt-2 pt-2 border-t border-cyan-500/20 font-mono text-[10px] grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-muted-foreground uppercase tracking-wider">Status</div>
          <div className={statusTone}>
            {running ? 'RUNNING' : (status || 'idle').toUpperCase()}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground uppercase tracking-wider">Last run</div>
          <div className="text-foreground">{fmtIso(state?.last_run_at)}</div>
        </div>
        <div>
          <div className="text-muted-foreground uppercase tracking-wider">Next scheduled</div>
          <div className="text-foreground">{fmtIso(state?.next_scheduled_run)}</div>
        </div>
        <div>
          <div className="text-muted-foreground uppercase tracking-wider">Duration</div>
          <div className="text-foreground">
            {state?.last_run_summary?.duration_seconds
              ? `${Math.round(state.last_run_summary.duration_seconds)}s`
              : '—'}
          </div>
        </div>
      </div>
      {Object.keys(profiles).length > 0 && (
        <div className="mt-2 pt-2 border-t border-cyan-500/20 font-mono text-[10px]">
          <div className="text-muted-foreground uppercase tracking-wider mb-1">
            Per-profile (last run)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {Object.entries(profiles).map(([key, p]) => (
              <div key={key} className="border border-border/40 px-2 py-1 bg-surface">
                <div className="flex items-center justify-between">
                  <span className="uppercase tracking-wider text-foreground">{key}</span>
                  <span className={
                    p.status === 'ok' ? 'text-emerald-300'
                      : p.status === 'error' ? 'text-rose-300'
                        : 'text-amber-300'
                  }>
                    {p.status}
                  </span>
                </div>
                {p.scoring && (
                  <div className="text-muted-foreground mt-0.5">
                    scored {p.scoring.scored ?? 0} · qualified {p.scoring.qualified ?? 0}
                  </div>
                )}
                {p.error && (
                  <div className="text-rose-300 mt-0.5 truncate" title={p.error}>
                    {p.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {state?.last_run_error && (
        <div className="mt-2 pt-2 border-t border-cyan-500/20 font-mono text-[10px] text-rose-300">
          Last error: {state.last_run_error}
        </div>
      )}
    </div>
  );
};


// ==================================================================
// Filters bar
// ==================================================================
const FilterBar = ({
  days, setDays,
  minValue, setMinValue,
  symbol, setSymbol,
  minConfidence, setMinConfidence,
  hideNoValue, setHideNoValue,
  customerTypes, toggleCustomerType,
  sizeTier, setSizeTier,
  verdictFilter, setVerdictFilter,
  hotStreakOnly, setHotStreakOnly,
  onScan, busy,
  readOnly = false,                  // 147j+16bo Ship 2 — hide scan button when true
}) => (
  <div
    className="flex items-center gap-2 flex-wrap"
    data-testid="lodr-filter-bar"
  >
    <Filter className="w-4 h-4 text-muted-foreground" />
    <select
      value={days}
      onChange={(e) => setDays(Number(e.target.value))}
      className="bg-surface border border-border px-2 py-1 text-[11px] font-mono"
      data-testid="lodr-filter-days"
    >
      {[1, 3, 7, 14, 30, 60, 90].map((d) => (
        <option key={d} value={d}>Last {d}d</option>
      ))}
    </select>
    <input
      type="text"
      value={symbol}
      onChange={(e) => setSymbol(e.target.value.toUpperCase())}
      placeholder="Search symbol or company"
      className="bg-surface border border-border px-2 py-1 text-[11px] font-mono w-32"
      data-testid="lodr-filter-symbol"
    />
    <input
      type="number"
      value={minValue}
      onChange={(e) => setMinValue(e.target.value)}
      placeholder="Min ₹Cr"
      className="bg-surface border border-border px-2 py-1 text-[11px] font-mono w-24"
      data-testid="lodr-filter-min-value"
    />
    <select
      value={minConfidence}
      onChange={(e) => setMinConfidence(Number(e.target.value))}
      className="bg-surface border border-border px-2 py-1 text-[11px] font-mono"
      data-testid="lodr-filter-min-confidence"
      title="Hide rows below this LLM confidence"
    >
      {[0, 20, 30, 50, 70, 90].map((c) => (
        <option key={c} value={c}>Conf ≥ {c}%</option>
      ))}
    </select>
    <select
      value={sizeTier}
      onChange={(e) => setSizeTier(e.target.value)}
      className="bg-surface border border-border px-2 py-1 text-[11px] font-mono"
      data-testid="lodr-filter-size-tier"
      title="Filter by order size relative to TTM revenue"
    >
      <option value="ALL">Size: All</option>
      <option value="small_plus">Size ≥ small (1%+)</option>
      <option value="medium_plus">Size ≥ medium (5%+)</option>
      <option value="large_plus">Size ≥ large (15%+)</option>
      <option value="huge">Size = huge (≥30%)</option>
    </select>
    <select
      value={verdictFilter}
      onChange={(e) => setVerdictFilter(e.target.value)}
      className="bg-surface border border-border px-2 py-1 text-[11px] font-mono"
      data-testid="lodr-filter-verdict"
      title="Filter rows by Phase 2 LLM verdict"
    >
      <option value="ALL">Verdict: All</option>
      <option value="BUY">Verdict: BUY only</option>
      <option value="WATCH">Verdict: WATCH only</option>
      <option value="SKIP">Verdict: SKIP only</option>
      <option value="AVOID">Verdict: AVOID only</option>
      <option value="PENDING">Verdict: pending (no Phase 2 yet)</option>
    </select>
    <label
      className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground cursor-pointer select-none"
      data-testid="lodr-filter-hide-no-value-label"
    >
      <input
        type="checkbox"
        checked={hideNoValue}
        onChange={(e) => setHideNoValue(e.target.checked)}
        className="accent-emerald-500"
        data-testid="lodr-filter-hide-no-value"
      />
      Has ₹ value
    </label>
    {/* 147j+16aw — Hot streak filter. Surfaces companies with 2+
        orders in 30d — a strong momentum signal. */}
    <label
      className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-amber-300/80 cursor-pointer select-none"
      data-testid="lodr-filter-hot-streak-label"
      title="Show only rows where the company won ≥2 orders in the last 30 days (excluding the current row)"
    >
      <input
        type="checkbox"
        checked={hotStreakOnly}
        onChange={(e) => setHotStreakOnly(e.target.checked)}
        className="accent-amber-500"
        data-testid="lodr-filter-hot-streak"
      />
      📈 Hot streak
    </label>
    <div
      className="inline-flex items-center gap-1"
      data-testid="lodr-filter-customer-types"
    >
      {['PSU', 'GOVERNMENT_DEFENCE', 'GOVERNMENT_OTHER',
        'PRIVATE_DOMESTIC', 'PRIVATE_INTERNATIONAL', 'UNKNOWN'].map((t) => {
        const active = customerTypes.includes(t);
        return (
          <button
            key={t}
            type="button"
            onClick={() => toggleCustomerType(t)}
            className={
              `px-2 py-1 text-[10px] font-mono uppercase border tracking-wider transition-opacity ` +
              (active
                ? (customerTypeTone[t] || 'border-border text-foreground')
                : 'border-border text-muted-foreground bg-surface opacity-60 hover:opacity-100')
            }
            data-testid={`lodr-filter-ctype-${t}`}
            title={active ? `Hide ${t}` : `Show ${t}`}
          >
            {t.replace('GOVERNMENT_', 'GOV ').replace('PRIVATE_', 'PVT ')}
          </button>
        );
      })}
    </div>
    {/* Session 147j+16bo Ship 2 — Scan button is a POST. Hidden in read-only mode. */}
    {!readOnly && (
      <button
        onClick={onScan}
        disabled={busy}
        className="ml-auto inline-flex items-center gap-1 px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
        data-testid="lodr-scan-btn"
      >
        <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
        Scan now
      </button>
    )}
  </div>
);

// ==================================================================
// 147j+16ce — Phase 2 cron pause/resume widget. Self-contained: fetches
// its own status, owns its own state. Sits between StatsBar and
// FilterBar in admin view. The cron defaults to PAUSED in this build
// (after a credit-burn incident). Click "Resume" to re-enable; click
// "Pause" again any time to stop fresh LLM verdict generation.
// ==================================================================
// ==================================================================
// 147j+17k — Pipeline health banner (persistent, top-of-tab).
//
// The user needs one glance to know:
//   1. Is Order Wins running? (green/amber/red)
//   2. If not — WHY? (credit_exhausted / syntax / code_error / other)
//   3. What's the current LLM credit balance for today?
//
// Polls GET /api/admin/lodr/cron-health every 60s. Silently degrades
// if the endpoint 404s (backend not yet deployed).
// ==================================================================
const CronHealthCapPill = ({ label, snap }) => {
  if (!snap) return null;
  const used = snap.calls_today || 0;
  const cap = snap.hard_cap || snap.soft_cap || 0;
  const ratio = cap ? Math.min(1, used / cap) : 0;
  const tone = snap.hard_exhausted
    ? 'bg-rose-500/25 text-rose-100 border-rose-500/60'
    : snap.soft_exhausted
      ? 'bg-amber-500/25 text-amber-100 border-amber-500/60'
      : 'bg-emerald-500/15 text-emerald-100 border-emerald-500/40';
  return (
    <div
      className={`px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider ${tone}`}
      title={`${label}: ${used} calls today (soft ${snap.soft_cap}, hard ${snap.hard_cap})`}
      data-testid={`lodr-cron-health-cap-${label.toLowerCase()}`}
    >
      {label} {used}/{cap}
      <span className="ml-1 opacity-60">
        {snap.credits_today ? `≈${snap.credits_today.toFixed(2)}c` : ''}
      </span>
      <div className="mt-0.5 h-0.5 bg-black/25 rounded overflow-hidden">
        <div
          className={snap.hard_exhausted ? 'bg-rose-400 h-full' : snap.soft_exhausted ? 'bg-amber-300 h-full' : 'bg-emerald-400 h-full'}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
    </div>
  );
};

const CronHealthBanner = () => {
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(
          `${API}/api/admin/lodr/cron-health`,
          { headers: authHeaders() },
        );
        if (!cancelled) { setHealth(r.data); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status || 'error');
      }
    };
    load();
    const t = setInterval(load, 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (err && !health) return null; // silent degradation
  if (!health) return null;

  const banner = health.banner || {};
  const severity = banner.severity || 'unknown';
  const budgets = health.llm_budgets || {};
  const spend = health.llm_spend_today || {};
  const crons = health.crons || {};
  const scanner = crons.lodr_scanner || {};
  const phase2 = crons.lodr_phase2 || {};

  const toneMap = {
    ok:       'border-emerald-500/50 bg-emerald-500/10 text-emerald-100',
    warning:  'border-amber-500/60 bg-amber-500/12 text-amber-100',
    critical: 'border-rose-500/70 bg-rose-500/15 text-rose-100',
    unknown:  'border-slate-500/40 bg-slate-500/10 text-slate-200',
  };
  const iconMap = { ok: '✅', warning: '⚠️', critical: '🚨', unknown: '⏳' };

  const fmtMinsAgo = (m) => {
    if (m == null) return '—';
    if (m < 1) return 'just now';
    if (m < 60) return `${Math.round(m)}m ago`;
    const h = Math.floor(m / 60), rm = Math.round(m % 60);
    return rm ? `${h}h ${rm}m ago` : `${h}h ago`;
  };

  return (
    <div
      className={`rounded border-2 p-3 ${toneMap[severity] || toneMap.unknown}`}
      data-testid="lodr-cron-health-banner"
      data-severity={severity}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="text-2xl leading-none">{iconMap[severity]}</div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-bold text-sm" data-testid="lodr-cron-health-banner-title">
            {banner.message || 'Pipeline status'}
          </div>
          {(banner.reasons || []).length > 0 && (
            <ul className="mt-0.5 text-[11px] opacity-90 list-disc list-inside space-y-0.5" data-testid="lodr-cron-health-banner-reasons">
              {banner.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          <div className="mt-1.5 text-[10px] font-mono uppercase tracking-wider opacity-80 flex flex-wrap gap-x-3 gap-y-0.5">
            <span data-testid="lodr-cron-health-scanner-status">
              Order Wins: {scanner.status || 'idle'}
              {scanner.last_success_at ? ` · last ok ${fmtMinsAgo(scanner.minutes_since_success)}` : ''}
              {scanner.last_error_reason ? ` · ${scanner.last_error_reason}` : ''}
            </span>
            <span data-testid="lodr-cron-health-phase2-status">
              Phase 2: {phase2.status || 'idle'}
              {phase2.last_success_at ? ` · last ok ${fmtMinsAgo(phase2.minutes_since_success)}` : ''}
              {phase2.last_error_reason ? ` · ${phase2.last_error_reason}` : ''}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <div
            className="text-[10px] font-mono uppercase tracking-wider opacity-80"
            data-testid="lodr-cron-health-credit-total"
            title="Today's total estimated Emergent LLM credits used across all sources."
          >
            LLM today: <b>{(spend.total_credits ?? 0).toFixed(2)}</b> credits · {spend.total_calls || 0} calls
          </div>
          <div className="flex flex-wrap gap-1.5">
            <CronHealthCapPill label="EXTR" snap={budgets.lodr_extraction} />
            <CronHealthCapPill label="VERD" snap={budgets.lodr_phase2_verdict} />
          </div>
        </div>
      </div>
      {scanner.last_traceback && severity === 'critical' && (
        <details className="mt-2 text-[10px] font-mono opacity-80" data-testid="lodr-cron-health-scanner-trace">
          <summary className="cursor-pointer">Scanner traceback</summary>
          <pre className="mt-1 whitespace-pre-wrap bg-black/25 p-2 rounded max-h-32 overflow-auto">{scanner.last_traceback}</pre>
        </details>
      )}
      {phase2.last_traceback && severity === 'critical' && (
        <details className="mt-2 text-[10px] font-mono opacity-80" data-testid="lodr-cron-health-phase2-trace">
          <summary className="cursor-pointer">Phase 2 traceback</summary>
          <pre className="mt-1 whitespace-pre-wrap bg-black/25 p-2 rounded max-h-32 overflow-auto">{phase2.last_traceback}</pre>
        </details>
      )}
    </div>
  );
};


const Phase2CronToggle = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/lodr/deep-analysis/phase2/cron/status`,
        { headers: authHeaders() },
      );
      setStatus(r.data || {});
    } catch (e) {
      // Endpoint is brand new — silently degrade if backend hasn't
      // deployed yet so the rest of the tab still loads.
      setStatus(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/lodr/deep-analysis/phase2/cron/${action}`,
        {},
        { headers: authHeaders() },
      );
      toast.success(
        action === 'pause'
          ? 'Phase 2 cron paused — no new LLM verdicts until you resume'
          : 'Phase 2 cron resumed — verdicts will refresh on next 30-min cycle',
      );
      await load();
    } catch (e) {
      toast.error(`Failed to ${action}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  // Effective state: runtime override wins, then env flag, then code default.
  const runtimePaused = status.paused;          // true | false | null
  const envDisabled = (status.env_disable_flag || '').toLowerCase() === 'true';
  const isPaused = runtimePaused === true || (runtimePaused == null && envDisabled !== false);

  const tone = isPaused
    ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
    : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200';

  return (
    <div
      className={`rounded border p-3 text-xs flex flex-wrap items-center gap-3 ${tone}`}
      data-testid="phase2-cron-toggle"
    >
      <div className="flex items-center gap-1.5 font-mono uppercase tracking-wider">
        {isPaused ? <Pause className="w-3.5 h-3.5" /> : <Activity className="w-3.5 h-3.5" />}
        Phase 2 verdict cron — {isPaused ? 'PAUSED' : 'RUNNING'}
      </div>

      <div className="flex-1 text-[11px] text-muted-foreground">
        {isPaused ? (
          <>
            No new LLM bullishness verdicts will be generated. Existing
            verdicts are unaffected. Cap: <span className="font-mono">{status.max_attempts}</span> retries
            per row · <span className="font-mono">{status.cutoff_days}d</span> cutoff.
          </>
        ) : (
          <>
            Cron is scoring new orders. Cap: <span className="font-mono">{status.max_attempts}</span> retries
            per row · <span className="font-mono">{status.cutoff_days}d</span> cutoff. Pause to
            stop LLM spend instantly.
          </>
        )}
      </div>

      {isPaused ? (
        <button
          onClick={() => toggle('resume')}
          disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-emerald-500/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50"
          data-testid="phase2-cron-resume-btn"
        >
          <Play className="w-3 h-3" /> Resume
        </button>
      ) : (
        <button
          onClick={() => toggle('pause')}
          disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-amber-500/60 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 disabled:opacity-50"
          data-testid="phase2-cron-pause-btn"
        >
          <Pause className="w-3 h-3" /> Pause cron
        </button>
      )}
    </div>
  );
};

// ==================================================================
// 147j+16cf — LODR scanner pause/resume + today's extraction usage.
// Mirrors Phase2CronToggle but gates the LLM PDF-extraction step.
// Also displays today's extraction-call usage vs the daily cap so
// the admin can spot when calls are queueing up at the cap line.
// ==================================================================
const LodrScannerToggle = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/lodr/scanner/status`,
        { headers: authHeaders() },
      );
      setStatus(r.data || {});
    } catch (e) {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh every 60s so the usage counter is current.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/lodr/scanner/${action}`,
        {},
        { headers: authHeaders() },
      );
      toast.success(
        action === 'pause'
          ? 'LODR scanner paused — new filings will store with NO LLM extraction'
          : 'LODR scanner resumed — new filings will be extracted via LLM',
      );
      await load();
    } catch (e) {
      toast.error(`Failed to ${action}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const isPaused = status.paused === true;
  const usage = status.usage || {};
  const capReached = !!usage.cap_reached;
  const pct = usage.cap > 0
    ? Math.min(100, Math.round((usage.count / usage.cap) * 100))
    : 0;

  const tone = isPaused
    ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
    : capReached
      ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
      : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200';

  return (
    <div
      className={`rounded border p-3 text-xs flex flex-wrap items-center gap-3 ${tone}`}
      data-testid="lodr-scanner-toggle"
    >
      <div className="flex items-center gap-1.5 font-mono uppercase tracking-wider">
        {isPaused
          ? <Pause className="w-3.5 h-3.5" />
          : capReached
            ? <AlertCircle className="w-3.5 h-3.5" />
            : <Activity className="w-3.5 h-3.5" />}
        LODR scanner — {isPaused
          ? 'PAUSED'
          : capReached
            ? 'CAP REACHED'
            : 'RUNNING'}
      </div>

      <div className="flex-1 text-[11px] text-muted-foreground">
        {isPaused ? (
          <>
            LLM extraction is OFF. Filings still flow to DB but show as
            "needs extraction". Click Resume to restore.
          </>
        ) : capReached ? (
          <>
            Today's extraction cap hit (<span className="font-mono">
              {usage.count}/{usage.cap}
            </span>). New filings queue without LLM until tomorrow
            (IST) or a manual re-extract.
          </>
        ) : (
          <>
            Extractions today: <span
              className="font-mono"
              data-testid="lodr-scanner-usage"
            >{usage.count}/{usage.cap}</span> ({pct}%).
            Pause to stop LLM spend instantly.
          </>
        )}
        {/* 147j+16cv — Per-source breakdown so the admin knows WHO is
            burning the cap (scanner_ingest vs hybrid_customer_fill vs
            reextract_pdf vs major_customer_backfill). */}
        {(Object.keys(usage.sources || {}).length > 0
          || Object.keys(usage.bypassed_sources || {}).length > 0) && (
          <div
            className="mt-1.5 font-mono text-[10px] text-zinc-400 leading-tight"
            data-testid="lodr-scanner-source-breakdown"
          >
            {Object.keys(usage.sources || {}).length > 0 && (
              <div>
                <span className="text-rose-300">counts cap:</span>{' '}
                {Object.entries(usage.sources)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => `${k}=${v}`)
                  .join(' · ')}
              </div>
            )}
            {Object.keys(usage.bypassed_sources || {}).length > 0 && (
              <div>
                <span className="text-emerald-300">bypassed:</span>{' '}
                {Object.entries(usage.bypassed_sources)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => `${k}=${v}`)
                  .join(' · ')}
              </div>
            )}
          </div>
        )}
      </div>

      {isPaused ? (
        <button
          onClick={() => toggle('resume')}
          disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-emerald-500/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50"
          data-testid="lodr-scanner-resume-btn"
        >
          <Play className="w-3 h-3" /> Resume
        </button>
      ) : (
        <button
          onClick={() => toggle('pause')}
          disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-amber-500/60 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 disabled:opacity-50"
          data-testid="lodr-scanner-pause-btn"
        >
          <Pause className="w-3 h-3" /> Pause scanner
        </button>
      )}
    </div>
  );
};

// ==================================================================
// Orders table
// ==================================================================
const OrdersTable = ({ rows, onDiagnose, readOnly = false, onRowRefresh }) => {
  // Session 147j+16au — switched expanded row from inline to a
  // right-side Drawer. Inline expansion lived inside the table's
  // horizontal-overflow context, so wide tables clipped the
  // thesis/bull/bear text at the right edge no matter how aggressive
  // we made the CSS `min-w-0` / `break-words`. Drawer sits at the
  // viewport's right edge, always fits, and exposes prev/next
  // arrows for fast row review.
  const [drawerRowId, setDrawerRowId] = React.useState(null);
  // Session 147j+17 — Refetch the currently-open row so late-arriving
  // Phase 2 verdicts (30-min cron cadence) surface without forcing the
  // user to reload the whole page. Falls back to the parent's cached
  // `rows[]` entry if the fetch fails.
  const [freshRow, setFreshRow] = React.useState(null);
  const openDrawer = React.useCallback((id) => setDrawerRowId(id), []);
  const closeDrawer = React.useCallback(() => { setDrawerRowId(null); setFreshRow(null); }, []);
  const drawerIdx = React.useMemo(
    () => (drawerRowId ? rows.findIndex((r) => r._id === drawerRowId) : -1),
    [rows, drawerRowId],
  );
  const cachedRow = drawerIdx >= 0 ? rows[drawerIdx] : null;
  const drawerRow = (freshRow && freshRow._id === drawerRowId) ? freshRow : cachedRow;

  // Fetch fresh row when drawer opens or navigates via prev/next arrows.
  React.useEffect(() => {
    if (!drawerRowId) return;
    let cancelled = false;
    setFreshRow(null);
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/admin/lodr/orders/${encodeURIComponent(drawerRowId)}?_ts=${Date.now()}`,
          { headers: authHeaders(), cache: 'no-store' },
        );
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data && data._id === drawerRowId) {
          setFreshRow(data);
          // Also update the parent's rows[] so the closed-drawer table
          // cell (verdict chip, EXTR/VERD %, red-flag) re-renders.
          if (typeof onRowRefresh === 'function') onRowRefresh(data);
        }
      } catch (e) {
        // Silent — the drawer will fall back to the cached row.
      }
    })();
    return () => { cancelled = true; };
  }, [drawerRowId, onRowRefresh]);
  const gotoPrev = React.useCallback(() => {
    if (drawerIdx > 0) setDrawerRowId(rows[drawerIdx - 1]._id);
  }, [drawerIdx, rows]);
  const gotoNext = React.useCallback(() => {
    if (drawerIdx >= 0 && drawerIdx < rows.length - 1) {
      setDrawerRowId(rows[drawerIdx + 1]._id);
    }
  }, [drawerIdx, rows]);
  // Keyboard nav inside drawer
  React.useEffect(() => {
    if (!drawerRow) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') gotoPrev();
      else if (e.key === 'ArrowRight') gotoNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerRow, gotoPrev, gotoNext]);
  if (!rows || rows.length === 0) {
    return (
      <div
        className="border border-border bg-surface p-6 text-center text-xs text-muted-foreground"
        data-testid="lodr-orders-empty"
      >
        No order announcements match these filters. Try widening the
        date range, or click <span className="text-foreground">Scan now</span> to fetch fresh data.
      </div>
    );
  }
  return (
    <div className="border border-border bg-surface overflow-x-auto" data-testid="lodr-orders-table">
      <table className="w-full text-[11px] font-mono">
        <thead className="bg-surface-highlight">
          <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="w-8 px-2 py-2" title="Click chevron to expand for full Phase 2 analysis"></th>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Symbol</th>
            <th className="px-3 py-2 text-center" title="Manipulation-risk flag — fires when order value is disproportionately large vs the stock's 30-day avg daily turnover. Common pattern in pump-and-dump operations.">🚨 Risk</th>
            <th className="px-3 py-2 text-left">Company</th>
            <th className="px-3 py-2 text-right">Value</th>
            <th className="px-3 py-2 text-right">% of TTM</th>
            <th className="px-3 py-2 text-right" title="Order value as a percentage of latest market cap">% of MCap</th>
            <th className="px-3 py-2 text-center">Type</th>
            <th className="px-3 py-2 text-left">Customer</th>
            <th className="px-3 py-2 text-center" title="Short / Long term bullishness, 0-10 each (LLM-derived)">Bullishness</th>
            <th className="px-3 py-2 text-center">Conf</th>
            <th className="px-3 py-2 text-center">Doc</th>
            <th className="px-3 py-2 text-center">Diag</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const le = r.llm_extracted || {};
            const ot = le.order_type || 'ORDER';
            const ct = le.customer_type || 'UNKNOWN';
            const conf = le.confidence;
            const summary = (le.summary || '').trim();
            const needsReview = !r.llm_extracted || le._needs_review === true;
            // Detect Tier A / Wave 2 modifier prefixes so we can surface
            // them next to the headline value (otherwise users have to
            // click the diagnose button to see the JV ratio / GST basis).
            const sLower = summary.toLowerCase();
            const valueModifiers = [];
            if (sLower.includes('jv ') || sLower.includes('jv:')) {
                // Try to extract the JV-total figure for a richer tooltip
                // (regex finds "JV X% of ₹Y Cr total").
                const m = summary.match(/jv\s*\d+\s*%\s*of\s*[₹]?\s*([0-9.,]+)\s*cr/i);
                valueModifiers.push({
                  label: 'JV',
                  tone: 'border-indigo-400/60 text-indigo-200 bg-indigo-500/10',
                  tip: m ? `JV — total contract ₹${m[1]} Cr` : 'Joint Venture share',
                });
            }
            if (sLower.startsWith('ex-gst:')) {
                valueModifiers.push({
                  label: 'ex-GST',
                  tone: 'border-emerald-400/60 text-emerald-200 bg-emerald-500/10',
                  tip: 'Value is exclusive of GST',
                });
            } else if (sLower.startsWith('incl-gst:')) {
                valueModifiers.push({
                  label: 'incl-GST',
                  tone: 'border-amber-400/60 text-amber-200 bg-amber-500/10',
                  tip: 'Value is inclusive of GST',
                });
            }
            if (sLower.startsWith('approx:') || sLower.startsWith('approx ')) {
                valueModifiers.push({
                  label: 'approx',
                  tone: 'border-amber-400/60 text-amber-200 bg-amber-500/10',
                  tip: 'Ceiling / approximate value — actual execution may differ',
                });
            }
            if (sLower.startsWith('range:')) {
                valueModifiers.push({
                  label: 'range',
                  tone: 'border-sky-400/60 text-sky-200 bg-sky-500/10',
                  tip: 'Value quoted as a range; shown as midpoint',
                });
            }
            // Session 147j+16al — qual-only badge for null-value rows
            // whose Phase 2 verdict came from the qualitative branch.
            const isQualOnly = (
              (le.order_value_cr == null)
              && (r?.deep_analysis?.phase2?._is_qualitative)
            );
            if (isQualOnly) {
              valueModifiers.push({
                label: 'qual-only',
                tone: 'border-orange-400/60 text-orange-200 bg-orange-500/10',
                tip: 'No disclosed contract value — verdict derived from structural signals (PSU buyer, concession tenure, scope language). Confidence capped at 0.5.',
              });
            }
            if (le.contract_status === 'CONDITIONAL_MOU') {
              valueModifiers.push({
                label: 'MOU',
                tone: 'border-amber-400/60 text-amber-200 bg-amber-500/10',
                tip: 'Conditional MOU — value reflects the firm Year-1 planning amount only; later years are indicative.',
              });
            }
            // Session 147j+16af — Deep Analysis Phase 1 surfacing.
            // Renders size_tier next to the value and value_pct_of_mcap
            // as a new column. Falls back gracefully when the row has
            // not been processed yet (value < ₹1 Cr or pre-phase1 row).
            const dp1 = (r.deep_analysis && r.deep_analysis.phase1) || null;
            const sizeTierTone = {
              small:  'border-zinc-400/40 text-zinc-300 bg-zinc-500/10',
              medium: 'border-sky-400/60 text-sky-200 bg-sky-500/10',
              large:  'border-amber-400/60 text-amber-200 bg-amber-500/10',
              huge:   'border-fuchsia-400/60 text-fuchsia-200 bg-fuchsia-500/10',
            };
            const isOpenInDrawer = drawerRowId === r._id;
            return (
              <React.Fragment key={r._id}>
              <tr
                className={`border-b border-border/40 hover:bg-surface-highlight cursor-pointer ${
                  isOpenInDrawer ? 'bg-surface-highlight/60' : ''
                }`}
                data-testid={`lodr-order-row-${r._id}`}
                onClick={(e) => {
                  // Don't trigger drawer when clicking nested interactive
                  // elements (diagnose btn, doc link, filter chips).
                  const t = e.target;
                  if (t.closest('button,a,input,select')) return;
                  openDrawer(r._id);
                }}
              >
                <td className="px-2 py-2 text-center">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openDrawer(r._id); }}
                    className="text-muted-foreground hover:text-foreground inline-flex items-center justify-center"
                    title="Open full analysis in drawer"
                    data-testid={`lodr-expand-btn-${r._id}`}
                    aria-expanded={isOpenInDrawer}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{fmtDate(r.announcement_date)}</td>
                <td className="px-3 py-2 font-bold text-foreground">{r.nse_symbol || '—'}</td>
                <td className="px-3 py-2 text-center" data-testid={`lodr-risk-cell-${r._id}`}>
                  {(() => {
                    const risk = r.manipulation_risk;
                    if (!risk || !risk.flag) {
                      return <span className="text-muted-foreground/40 text-[10px]">—</span>;
                    }
                    const tone = {
                      CRITICAL: 'bg-rose-500/25 text-rose-100 border-rose-400/70 animate-pulse',
                      HIGH:     'bg-orange-500/20 text-orange-200 border-orange-400/60',
                      WATCH:    'bg-amber-500/15 text-amber-200 border-amber-500/50',
                    }[risk.flag] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30';
                    const emoji = risk.flag === 'CRITICAL' ? '🚨' : risk.flag === 'HIGH' ? '⚠️' : '📢';
                    return (
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider border rounded font-semibold ${tone}`}
                        title={risk.narrative || `${risk.flag} · ${risk.days_to_absorb}× turnover · ${risk.tier}`}
                        data-testid={`lodr-risk-flag-${r._id}`}
                      >
                        {emoji} {risk.flag}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 text-foreground/80 max-w-[200px] truncate">{r.company_name}</td>
                <td
                  className="px-3 py-2 text-right"
                  title={summary || undefined}
                  data-testid={`lodr-value-cell-${r._id}`}
                >
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-emerald-300 font-bold">
                      {fmtCr(le.order_value_cr)}
                    </span>
                    {valueModifiers.length > 0 && (
                      <div className="flex gap-1 flex-wrap justify-end">
                        {valueModifiers.map((mod) => (
                          <span
                            key={mod.label}
                            title={mod.tip}
                            className={`px-1 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm ${mod.tone}`}
                            data-testid={`lodr-value-modifier-${mod.label}-${r._id}`}
                          >
                            {mod.label}
                          </span>
                        ))}
                      </div>
                    )}
                    {dp1 && dp1.size_tier && (
                      <span
                        title={`Size tier: ${dp1.size_tier} (${fmtPct(r.value_pct_of_ttm)} of TTM)`}
                        className={`mt-0.5 px-1 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm ${sizeTierTone[dp1.size_tier] || sizeTierTone.small}`}
                        data-testid={`lodr-size-tier-${dp1.size_tier}`}
                      >
                        {dp1.size_tier}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-foreground">
                  {fmtPct(r.value_pct_of_ttm)}
                </td>
                <td
                  className="px-3 py-2 text-right text-foreground"
                  title={
                    dp1 && dp1.market_cap_cr
                      ? `Market cap: ₹${Number(dp1.market_cap_cr).toFixed(0)} Cr`
                      : 'Market cap unavailable'
                  }
                  data-testid={`lodr-mcap-cell-${r._id}`}
                >
                  {dp1 && dp1.value_pct_of_mcap != null
                    ? fmtPct(dp1.value_pct_of_mcap)
                    : '—'}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider border ${orderTypeTone[ot] || orderTypeTone.ORDER}`}>
                    {ot}
                  </span>
                </td>
                <td className="px-3 py-2 max-w-[180px]">
                  <div className="flex items-center gap-1.5">
                    <div className="truncate text-foreground/80 flex-1">{le.customer || '—'}</div>
                    {needsReview && (
                      <span
                        title="The filing was stored but needs an extraction review before it can be ranked."
                        className="px-1 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm border-amber-400/60 text-amber-200 bg-amber-500/10 shrink-0"
                        data-testid={`lodr-needs-review-${r._id}`}
                      >
                        review
                      </span>
                    )}
                    {dp1 && dp1.is_repeat_customer && (
                      <span
                        title="Repeat customer — same buyer placed an order with this issuer in the last 90 days"
                        className="px-1 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm border-emerald-400/60 text-emerald-200 bg-emerald-500/10 shrink-0"
                        data-testid={`lodr-repeat-customer-${r._id}`}
                      >
                        repeat
                      </span>
                    )}
                    {/* 147j+16bp — Smart Money chip. Green dot when net
                        smart money is buying around the announcement,
                        red when selling. Tooltip surfaces top entity. */}
                    {r.smart_money && r.smart_money.matched_count > 0 && (() => {
                      const sm = r.smart_money;
                      const isBuy = sm.dominant_side === 'BUY';
                      const isSell = sm.dominant_side === 'SELL';
                      const dotColor = isBuy ? 'bg-emerald-400' : isSell ? 'bg-rose-400' : 'bg-amber-400';
                      const offsetTxt =
                        sm.top_offset_days === 0 ? 'T+0'
                          : sm.top_offset_days > 0 ? `T+${sm.top_offset_days}`
                          : `T${sm.top_offset_days}`;
                      const tip =
                        `${sm.top_name} ${sm.top_side || ''} ` +
                        `${_fmtSharesCompact(sm.top_qty)} sh (${offsetTxt})` +
                        (sm.matched_count > 1
                          ? ` · +${sm.matched_count - 1} more in ±${sm.window_days}d`
                          : '');
                      return (
                        <span
                          title={tip}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm border-violet-400/60 text-violet-200 bg-violet-500/10 shrink-0 font-bold"
                          data-testid={`lodr-smart-money-chip-${r._id}`}
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />
                          SMART MONEY
                        </span>
                      );
                    })()}
                    {/* 147j+17w — Data-driven Pre-LODR / MEDIA chip.
                        Chip style depends on actual lead-time vs the
                        BSE filing:
                          > 24h ahead  → ⚡ PRE-LODR (amber, real edge)
                          6-24h ahead  → 📰 MEDIA (sky, ambiguous)
                          0-6h ahead   → 📰 MEDIA · confirmed
                          negative lead → 📰 MEDIA · post-LODR (grey) */}
                    {r.pre_lodr_flag && r.pre_lodr_lead_hours != null && (() => {
                      const leadH = r.pre_lodr_lead_hours;
                      const tier =
                        leadH >= 24 ? 'PRE_LODR'
                          : leadH >= 6 ? 'MEDIA_EARLY'
                          : leadH >= 0 ? 'MEDIA_CONFIRM'
                          : 'MEDIA_LATE';
                      const style = {
                        PRE_LODR:      { cls: 'border-amber-400/60 text-amber-200 bg-amber-500/10', ic: '⚡', label: 'PRE-LODR' },
                        MEDIA_EARLY:   { cls: 'border-sky-400/60 text-sky-200 bg-sky-500/10',       ic: '📰', label: 'MEDIA' },
                        MEDIA_CONFIRM: { cls: 'border-sky-500/40 text-sky-300/80 bg-sky-500/5',      ic: '📰', label: 'MEDIA' },
                        MEDIA_LATE:    { cls: 'border-zinc-500/40 text-zinc-400 bg-zinc-500/10',     ic: '📰', label: 'MEDIA' },
                      }[tier];
                      const suffix =
                        tier === 'PRE_LODR'      ? `${(leadH / 24).toFixed(1)}d early`
                        : tier === 'MEDIA_EARLY' ? `${leadH.toFixed(1)}h early`
                        : tier === 'MEDIA_CONFIRM' ? 'confirmed'
                        : `post-LODR`;
                      const tip =
                        tier === 'PRE_LODR'
                          ? `⚡ Genuine lead — ${r.pre_lodr_source} reported this ${(leadH/24).toFixed(1)}d BEFORE BSE filing. Real information edge.`
                        : tier === 'MEDIA_EARLY'
                          ? `${r.pre_lodr_source} article published ${leadH.toFixed(1)}h before BSE — small but real lead.`
                        : tier === 'MEDIA_CONFIRM'
                          ? `${r.pre_lodr_source} article published within 6h of BSE filing — treats as cross-verification.`
                        : `${r.pre_lodr_source} article published ${Math.abs(leadH).toFixed(1)}h AFTER BSE — aggregator lagged, useful only for enrichment.`;
                      const chip = (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[8px] uppercase tracking-wider border rounded-sm shrink-0 font-bold ${style.cls}`}
                          data-testid={`lodr-pre-lodr-chip-${r._id}`}
                          title={tip}
                        >
                          <span className="leading-none">{style.ic}</span>
                          {style.label}
                          <span className="opacity-70 normal-case tracking-normal">·{suffix}</span>
                        </span>
                      );
                      return r.pre_lodr_link ? (
                        <a
                          href={r.pre_lodr_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="no-underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {chip}
                        </a>
                      ) : chip;
                    })()}
                  </div>
                  <span className={`inline-block mt-0.5 px-1 py-0.5 text-[9px] uppercase tracking-wider border ${customerTypeTone[ct] || customerTypeTone.UNKNOWN}`}>
                    {ct.replace('_', ' ').toLowerCase()}
                  </span>
                </td>
                <BullishnessCell row={r} />
                <td className="px-3 py-2 text-center">
                  {/* 147j+16ch — Explicit confidence labels.
                      Two separate numbers were being conflated under
                      one "CONF" column: extraction confidence (LLM's
                      certainty about value/customer/etc.) vs verdict
                      confidence (Phase 2's certainty about BUY/HOLD/
                      SKIP). On MINOR rows the verdict-conf is the
                      hard-coded 0.45 from the deterministic bundle —
                      showing it next to the extraction 100% makes
                      clear the verdict was skipped, not "100 % sure
                      this is LOW IMPACT". */}
                  {(() => {
                    const verdictConf = r?.deep_analysis?.phase2?.confidence;
                    const isDeterministic = !!r?.deep_analysis?.phase2?._deterministic;
                    if (conf == null && verdictConf == null) return '—';
                    return (
                      <div className="flex flex-col items-center gap-0.5 leading-tight">
                        {conf != null && (
                          <div className="flex items-baseline gap-1">
                            <span className="text-[9px] text-muted-foreground uppercase tracking-wider">extr</span>
                            <span className={
                              conf >= 0.8 ? 'text-emerald-300'
                                : conf >= 0.6 ? 'text-amber-300' : 'text-rose-300'
                            }>
                              {(conf * 100).toFixed(0)}%
                            </span>
                          </div>
                        )}
                        {verdictConf != null && (
                          <div className="flex items-baseline gap-1">
                            <span className="text-[9px] text-muted-foreground uppercase tracking-wider">
                              verd
                            </span>
                            <span
                              className={`${
                                verdictConf >= 0.8 ? 'text-emerald-300'
                                  : verdictConf >= 0.6 ? 'text-amber-300'
                                  : 'text-rose-300'
                              } ${isDeterministic ? 'italic opacity-70' : ''}`}
                              title={isDeterministic
                                ? 'Deterministic LOW IMPACT verdict — no AI call ran for this row'
                                : 'AI verdict confidence'}
                            >
                              {(verdictConf * 100).toFixed(0)}%
                              {isDeterministic && (
                                <span className="ml-1 text-[9px] text-muted-foreground">skipped</span>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.attachment_url ? (
                    <a
                      href={r.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-violet-300 hover:text-violet-200 inline-flex items-center"
                      data-testid={`lodr-pdf-link-${r._id}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : '—'}
                </td>
                <td className="px-3 py-2 text-center">
                  {/* Session 147j+16bo Ship 2 — Diagnose btn opens a
                      modal containing force-reextract POST. Hide in
                      read-only mode; show a non-actionable placeholder. */}
                  {readOnly ? (
                    <span className="text-muted-foreground/50">—</span>
                  ) : (
                    <button
                      onClick={() => onDiagnose && onDiagnose(r)}
                      className="text-amber-300 hover:text-amber-200 inline-flex items-center"
                      title="Diagnose why value is null / force re-extract"
                      data-testid={`lodr-diagnose-btn-${r._id}`}
                    >
                      <Search className="w-3 h-3" />
                    </button>
                  )}
                </td>
              </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {/* 147j+16au — right-side drawer with full Phase 2 analysis.
          Always fits viewport, supports prev/next nav via arrow keys
          or buttons. Replaces the in-row expansion that was clipping
          thesis/bull/bear text on wide tables. */}
      <Sheet
        open={!!drawerRow}
        onOpenChange={(open) => { if (!open) closeDrawer(); }}
      >
        <SheetContent
          side="right"
          className="!max-w-none sm:!max-w-none w-full sm:w-[640px] md:w-[720px] overflow-y-auto p-0"
          data-testid="lodr-detail-drawer"
        >
          {drawerRow && (
            <div className="flex flex-col h-full">
              <SheetHeader className="px-5 py-4 border-b border-border bg-surface-highlight sticky top-0 z-10">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <SheetTitle className="text-base font-bold tracking-wide flex items-center gap-2">
                      <span className="text-foreground">{drawerRow.nse_symbol || '—'}</span>
                      <span className="text-xs font-normal text-muted-foreground truncate">
                        {drawerRow.company_name}
                      </span>
                    </SheetTitle>
                    <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                      {fmtDate(drawerRow.announcement_date)} · row {drawerIdx + 1} of {rows.length}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={gotoPrev}
                      disabled={drawerIdx <= 0}
                      className="p-1 border border-border rounded hover:bg-surface disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Previous row (←)"
                      data-testid="lodr-drawer-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={gotoNext}
                      disabled={drawerIdx >= rows.length - 1}
                      className="p-1 border border-border rounded hover:bg-surface disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Next row (→)"
                      data-testid="lodr-drawer-next-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </SheetHeader>
              <div className="px-5 py-4 flex-1 overflow-y-auto">
                <DeepAnalysisCard row={drawerRow} readOnly={readOnly} />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// ==================================================================
// Session 147j+15b — Diagnose modal (per-row why-is-value-null + force re-extract)
// ==================================================================
const DiagnoseModal = ({ row, onClose, onRefresh }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const fetchDiag = useCallback(async () => {
    if (!row) return;
    setError(null);
    try {
      const r = await axios.get(
        `${API}/api/admin/lodr/diagnose?news_id=${encodeURIComponent(row._id)}`,
        { headers: authHeaders() },
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [row]);

  useEffect(() => { fetchDiag(); }, [fetchDiag]);

  const onForceReextract = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/lodr/force-reextract?news_id=${encodeURIComponent(row._id)}`,
        {},
        { headers: authHeaders() },
      );
      const status = r.data?.status;
      if (status === 'done') {
        toast.success(
          `Re-extracted: value=₹${r.data.order_value_cr ?? '—'} Cr · TTM=${r.data.value_pct_of_ttm ?? '—'}%`,
        );
      } else {
        toast.warning(`Status: ${status}`);
      }
      await fetchDiag();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(`Force-reextract failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!row) return null;
  const verdict = data?.verdict || '';
  const verdictTone = verdict.startsWith('OK')
    ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
    : verdict.startsWith('EXPIRED')
    ? 'border-rose-500/50 bg-rose-500/10 text-rose-300'
    : verdict.startsWith('NEVER_PROCESSED') || verdict.startsWith('LLM_NULL')
    ? 'border-amber-500/50 bg-amber-500/10 text-amber-300'
    : 'border-border bg-surface-highlight text-muted-foreground';

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lodr-diagnose-modal"
    >
      <div
        className="bg-surface border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider">
            <Wrench className="w-3 h-3 text-amber-300" />
            Diagnose · {row.nse_symbol || row._id}
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            data-testid="lodr-diagnose-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3 text-[11px] font-mono">
          {error && (
            <div className="px-2 py-1 text-rose-300 border border-rose-500/40 bg-rose-500/10">
              {error}
            </div>
          )}
          {!data && !error && (
            <div className="text-muted-foreground">Loading…</div>
          )}
          {data && data.found === false && (
            <div className="text-rose-300">Row not found in corp_orders.</div>
          )}
          {data && data.found !== false && (
            <>
              <div className={`px-3 py-2 border ${verdictTone}`} data-testid="lodr-diagnose-verdict">
                <div className="text-[10px] uppercase tracking-wider mb-1">Verdict</div>
                <div className="text-[11px]">{verdict}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider">PDF used</div>
                  <div className={data.pdf_used ? 'text-emerald-300' : 'text-rose-300'}>
                    {String(data.pdf_used)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider">PDF expired</div>
                  <div className={data.pdf_expired ? 'text-rose-300' : 'text-emerald-300'}>
                    {String(data.pdf_expired)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider">TTM revenue</div>
                  <div className="text-foreground">
                    {data.ttm_revenue_cr ? `₹${data.ttm_revenue_cr} Cr` : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase tracking-wider">% of TTM</div>
                  <div className="text-foreground">
                    {data.value_pct_of_ttm != null ? `${data.value_pct_of_ttm}%` : '—'}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Headline</div>
                <div className="text-foreground/80 break-words">{data.headline || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">LLM extracted</div>
                <pre className="text-[10px] bg-surface-highlight border border-border p-2 overflow-x-auto whitespace-pre-wrap">
                  {data.llm_extracted ? JSON.stringify(data.llm_extracted, null, 2) : 'null'}
                </pre>
              </div>
              {data.attachment_url && (
                <a
                  href={data.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-violet-300 hover:text-violet-200 text-[10px]"
                >
                  <ExternalLink className="w-3 h-3" /> Open PDF
                </a>
              )}
              <div className="pt-2 border-t border-border flex items-center gap-2">
                <button
                  onClick={onForceReextract}
                  disabled={busy || !data.attachment_url}
                  className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
                  data-testid="lodr-force-reextract-btn"
                >
                  <RefreshCw className={`w-3 h-3 inline mr-1 ${busy ? 'animate-spin' : ''}`} />
                  Force re-extract
                </button>
                <span className="text-[10px] text-muted-foreground">
                  Ignores `pdf_used` / `pdf_expired` and re-fetches the PDF.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================================================================
// Session 147j+16cj — Admin-only "Today's LLM Spend" panel.
// Shows the same number the user sees draining off the Universal Key
// balance, attributed to the actual feature that burned it
// (AlphaScout pipeline / LODR Phase 2 / LODR extraction).
// ==================================================================
const SOURCE_META = {
  alphascout_rationale: {
    label: 'AlphaScout pipeline',
    desc: 'Per-stock rationale generation during the daily 10:00 IST run.',
    accent: 'text-violet-300',
    bar: 'bg-violet-500',
  },
  lodr_phase2_verdict: {
    label: 'LODR Phase 2 verdicts',
    desc: 'AI Trader/Investor verdict on MAJOR-tier orders.',
    accent: 'text-cyan-300',
    bar: 'bg-cyan-500',
  },
  lodr_extraction: {
    label: 'LODR extraction',
    desc: 'LLM extraction of value/customer when regex parse fails (capped at 25/day).',
    accent: 'text-amber-300',
    bar: 'bg-amber-500',
  },
};

const LlmSpendPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(true);

  const fetchSpend = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/llm-spend/today`,
        { headers: authHeaders() },
      );
      if (res.data?.ok) setData(res.data);
      else setErr(res.data?.error || 'Unknown error');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSpend(); }, [fetchSpend]);

  const today = data?.today;
  const total = today?.total_credits ?? 0;
  const totalCalls = today?.total_calls ?? 0;
  const sources = today?.by_source ?? [];
  const maxCredits = Math.max(0.01, ...sources.map((s) => s.credits_est));
  const history = data?.history_7d ?? [];

  return (
    <div
      data-testid="admin-llm-spend-panel"
      className="rounded-xl border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-950/30 via-zinc-950/40 to-zinc-950/20 px-4 py-3 shadow-inner"
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left flex-1"
          data-testid="admin-llm-spend-toggle"
        >
          <IndianRupee className="w-4 h-4 text-fuchsia-300" />
          <span className="font-heading text-sm font-semibold text-fuchsia-100">
            Today&apos;s LLM Spend
          </span>
          <span className="text-xs font-mono text-zinc-400">
            ({today?.date || '—'} UTC, est.)
          </span>
          <span className="ml-3 text-xs text-zinc-500">
            {open ? '▾' : '▸'}
          </span>
        </button>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div
              className="text-2xl font-bold text-fuchsia-200 leading-none font-mono"
              data-testid="admin-llm-spend-total"
            >
              {total.toFixed(2)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
              credits · {totalCalls} call{totalCalls === 1 ? '' : 's'}
            </div>
          </div>
          <button
            type="button"
            onClick={fetchSpend}
            disabled={loading}
            className="px-2 py-1 text-xs rounded border border-zinc-700 hover:border-fuchsia-400/60 hover:text-fuchsia-200 transition disabled:opacity-40 font-mono"
            data-testid="admin-llm-spend-refresh"
          >
            {loading ? '…' : '⟳'}
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-2 text-xs text-rose-300 font-mono">
          {err}
        </div>
      )}

      {open && (
        <div className="mt-3 space-y-3">
          {sources.length === 0 && !loading && !err && (
            <div className="text-xs text-zinc-500 italic font-mono">
              No LLM spend recorded today yet. Counters reset at 00:00 UTC.
            </div>
          )}
          {sources.map((s) => {
            const meta = SOURCE_META[s.source] || {
              label: s.source, desc: '', accent: 'text-zinc-200',
              bar: 'bg-zinc-500',
            };
            const pct = (s.credits_est / maxCredits) * 100;
            return (
              <div
                key={s.source}
                data-testid={`admin-llm-spend-row-${s.source}`}
                className="space-y-1"
              >
                <div className="flex items-center justify-between gap-3 text-xs font-mono">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`font-semibold ${meta.accent}`}>
                      {meta.label}
                    </span>
                    <span className="text-zinc-500 truncate">
                      · {s.calls} call{s.calls === 1 ? '' : 's'}
                      {s.top_model ? ` · ${s.top_model}` : ''}
                    </span>
                  </div>
                  <div className="text-zinc-200 font-bold tabular-nums">
                    {s.credits_est.toFixed(3)} cr
                  </div>
                </div>
                <div className="h-1.5 w-full bg-zinc-800/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${meta.bar}/80 rounded-full transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {meta.desc && (
                  <div className="text-[10px] text-zinc-500 leading-tight">
                    {meta.desc}
                  </div>
                )}
              </div>
            );
          })}

          {history.length > 1 && (
            <div className="mt-4 pt-3 border-t border-zinc-800/60">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-mono">
                Last 7 days
              </div>
              <div className="flex items-end gap-1 h-12">
                {history.map((d) => {
                  const max = Math.max(
                    0.01, ...history.map((x) => x.credits_est),
                  );
                  const h = Math.max(2, (d.credits_est / max) * 100);
                  return (
                    <div
                      key={d.date}
                      className="flex-1 flex flex-col items-center justify-end gap-1"
                      title={`${d.date}: ${d.credits_est} cr · ${d.calls} calls`}
                    >
                      <div
                        className="w-full bg-fuchsia-500/60 rounded-sm transition-all"
                        style={{ height: `${h}%` }}
                      />
                      <div className="text-[9px] font-mono text-zinc-600">
                        {d.date.slice(5)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-[10px] text-zinc-600 font-mono leading-tight pt-1">
            Estimate based on public per-model token rates × 100 credits/USD.
            Actual Universal Key billing may vary slightly. Resets at 00:00 UTC.
          </div>
        </div>
      )}
    </div>
  );
};


// ==================================================================
// Session 147j+16cl — "Data cleanup" panel: one-click triggers for
// the v16ck regex backfill and per-symbol cross-exchange dedup.
// Admin-only. Lets the user repair the deployed production DB
// without curl/SSH after the regex hardening was shipped.
// ==================================================================
// ==================================================================
// Session 147j+16cn — Tiny typeahead for NSE-symbol inputs in the
// Data Cleanup panel. Debounces 200ms, hits the admin-gated
// `/api/admin/lodr/symbol-suggest?q=` endpoint, renders a small
// popup below the input with up to 10 matches.
// ==================================================================
const SymbolTypeahead = ({ value, onChange, placeholder, testIdPrefix }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/api/admin/lodr/symbol-suggest?q=${encodeURIComponent(value || '')}&limit=10`,
          { headers: authHeaders() },
        );
        setSuggestions(res.data?.symbols || []);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const pick = (sym) => {
    onChange(sym);
    setOpen(false);
  };

  return (
    <div className="relative flex-1">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value.toUpperCase()); setOpen(true); setHover(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || !suggestions.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHover((h) => Math.min(h + 1, suggestions.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(suggestions[hover]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
        placeholder={placeholder}
        className="w-full px-2 py-1.5 text-xs rounded bg-zinc-900/80 border border-zinc-700 focus:border-orange-400/60 outline-none font-mono"
        data-testid={`${testIdPrefix}-input`}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-20 left-0 right-0 top-full mt-1 max-h-56 overflow-auto rounded border border-zinc-700 bg-zinc-950/95 backdrop-blur-sm shadow-xl"
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHover(i)}
              className={`block w-full text-left px-3 py-1.5 text-xs font-mono ${
                i === hover ? 'bg-orange-500/20 text-orange-100' : 'text-zinc-300 hover:bg-zinc-800/60'
              }`}
              data-testid={`${testIdPrefix}-suggestion-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DataCleanupPanel = () => {
  const [usdReport, setUsdReport] = useState(null);
  const [usdLoading, setUsdLoading] = useState(false);
  const [dedupeSymbol, setDedupeSymbol] = useState('ASHOKA');
  const [dedupeReport, setDedupeReport] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  // 147j+17m — Global dedupe sweep (URL + value/customer) across the
  // entire recent window. Powers the "remove leaderboard dups" flow
  // triggered by the INDIANHUME × 2 case (2026-07-03).
  const [gDedupeDays, setGDedupeDays] = useState(30);
  const [gDedupeReport, setGDedupeReport] = useState(null);
  const [gDedupeLoading, setGDedupeLoading] = useState(false);
  const [forceSymbol, setForceSymbol] = useState('TRANSRAILL');
  const [forceReport, setForceReport] = useState(null);
  const [forceLoading, setForceLoading] = useState(false);
  // 147j+16co — Backfill customers for MAJOR rows + re-run Phase 2 per symbol
  const [majorBackfillReport, setMajorBackfillReport] = useState(null);
  const [majorBackfillLoading, setMajorBackfillLoading] = useState(false);
  const [majorDays, setMajorDays] = useState(30);
  const [majorMinValue, setMajorMinValue] = useState(250);
  const [majorLimit, setMajorLimit] = useState(25);
  const [phase2RerunSymbol, setPhase2RerunSymbol] = useState('');
  const [phase2RerunReport, setPhase2RerunReport] = useState(null);
  const [phase2RerunLoading, setPhase2RerunLoading] = useState(false);
  // 147j+16cp — Heal stale `value_pct_of_ttm` (Ashoka 936% bug)
  const [healStaleReport, setHealStaleReport] = useState(null);
  const [healStaleLoading, setHealStaleLoading] = useState(false);
  // 147j+16cs — Red-flag backfill (Vikran-style related-party detection)
  const [redFlagSymbol, setRedFlagSymbol] = useState('');
  const [redFlagReport, setRedFlagReport] = useState(null);
  const [redFlagLoading, setRedFlagLoading] = useState(false);
  const [open, setOpen] = useState(true);

  const runUsdFix = async (dryRun) => {
    setUsdLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/lodr/fix-bad-usd-extractions?dry_run=${dryRun}&min_value_cr=5000`,
        null,
        { headers: authHeaders() },
      );
      setUsdReport(res.data);
      if (!dryRun) {
        toast.success(
          `Fixed ${res.data.rows_updated} row(s), nulled ${res.data.rows_nulled_for_llm_retry} for LLM retry`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setUsdLoading(false);
    }
  };

  const runDedupe = async (dryRun) => {
    if (!dedupeSymbol.trim()) {
      toast.error('Enter an NSE symbol first');
      return;
    }
    setDedupeLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/lodr/dedupe-symbol?symbol=${encodeURIComponent(dedupeSymbol.trim())}&dry_run=${dryRun}`,
        null,
        { headers: authHeaders() },
      );
      setDedupeReport(res.data);
      if (!dryRun) {
        toast.success(
          `Merged ${res.data.merged_pairs} duplicate pair(s) for ${res.data.symbol}`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setDedupeLoading(false);
    }
  };

  // 147j+17m — Global dedupe sweep: runs the two-pass server-side
  // dedupe (URL match first, then value/customer bucket) across every
  // symbol in the recent window. This is the "recalculate leaderboard"
  // fix — the leaderboard just re-aggregates from `corp_orders`, so
  // deleting hidden dups automatically updates rankings on next load.
  const runGlobalDedupe = async (dryRun) => {
    setGDedupeLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/lodr/dedupe-existing-orders?days=${gDedupeDays}&dry_run=${dryRun}`,
        null,
        { headers: authHeaders() },
      );
      setGDedupeReport(res.data);
      if (!dryRun) {
        toast.success(
          `Removed ${res.data.deleted} duplicate row(s) — leaderboard will recalculate on next refresh.`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setGDedupeLoading(false);
    }
  };

  const runForceReextract = async (dryRun) => {
    if (!forceSymbol.trim()) {
      toast.error('Enter an NSE symbol first');
      return;
    }
    setForceLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/lodr/reextract-symbol?symbol=${encodeURIComponent(forceSymbol.trim())}&dry_run=${dryRun}`,
        null,
        { headers: authHeaders() },
      );
      setForceReport(res.data);
      if (!dryRun) {
        toast.success(
          `Updated ${res.data.rows_updated} row(s), nulled ${res.data.rows_nulled_for_llm_retry} for ${res.data.symbol}`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setForceLoading(false);
    }
  };

  // 147j+16co — Backfill customer/sector/summary on MAJOR rows whose
  // regex-only extraction left customer empty. Calls Gemini per row.
  const runMajorBackfill = async (dryRun) => {
    setMajorBackfillLoading(true);
    try {
      const params = new URLSearchParams({
        days: String(majorDays),
        min_value_cr: String(majorMinValue),
        limit: String(majorLimit),
        dry_run: String(dryRun),
      });
      const res = await axios.post(
        `${API}/api/admin/lodr/backfill-major-customers?${params.toString()}`,
        null,
        { headers: authHeaders() },
      );
      setMajorBackfillReport(res.data);
      if (!dryRun) {
        toast.success(
          `Backfilled ${res.data.rows_updated}/${res.data.candidates_found} MAJOR row(s)`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setMajorBackfillLoading(false);
    }
  };

  // 147j+16co — Re-run Phase 2 LLM verdict on every recent row for a
  // symbol (use after backfilling customers so MAJOR routing fires).
  const runPhase2Rerun = async (dryRun) => {
    if (!phase2RerunSymbol.trim()) {
      toast.error('Enter an NSE symbol first');
      return;
    }
    setPhase2RerunLoading(true);
    try {
      const params = new URLSearchParams({
        symbol: phase2RerunSymbol.trim(),
        days: '30',
        force: 'true',
        dry_run: String(dryRun),
      });
      const res = await axios.post(
        `${API}/api/admin/lodr/rerun-phase2-symbol?${params.toString()}`,
        null,
        { headers: authHeaders() },
      );
      setPhase2RerunReport(res.data);
      if (!dryRun) {
        toast.success(
          `Re-ran Phase 2 on ${res.data.rows_updated}/${res.data.candidates_found} row(s) for ${res.data.symbol}`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setPhase2RerunLoading(false);
    }
  };

  // 147j+16cp — Heal stale `value_pct_of_ttm` rows. Ashoka showed 936%
  // because the re-extract path forgot to recompute the percent.
  const runHealStaleTtmPct = async (dryRun) => {
    setHealStaleLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/lodr/heal-stale-value-pct?days=180&dry_run=${dryRun}`,
        null,
        { headers: authHeaders() },
      );
      setHealStaleReport(res.data);
      if (!dryRun) {
        toast.success(
          `Healed ${res.data.rows_updated}/${res.data.candidates_found} stale %TTM row(s)`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setHealStaleLoading(false);
    }
  };

  // 147j+16cs — Backfill red flags (Vikran-style related-party detector)
  // 147j+16cy — Bulk dedupe sweep across all symbols (Step 9)
  const [sweepReport, setSweepReport] = useState(null);
  const [sweepLoading, setSweepLoading] = useState(false);
  const runDedupeSweep = async (dryRun) => {
    setSweepLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/lodr/dedupe-sweep?days=30&dry_run=${dryRun}`,
        null,
        { headers: authHeaders() },
      );
      setSweepReport(res.data);
      if (!dryRun) {
        toast.success(
          `Hid ${res.data.rows_hidden} duplicate(s) across ${res.data.symbols_scanned} symbols`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSweepLoading(false);
    }
  };

  const runRedFlagBackfill = async (dryRun, includeInspector) => {
    setRedFlagLoading(true);
    try {
      const params = new URLSearchParams({
        days: '30',
        include_inspector: String(includeInspector),
        inspector_limit: '15',
        dry_run: String(dryRun),
      });
      if (redFlagSymbol.trim()) {
        params.set('symbol', redFlagSymbol.trim());
      }
      const res = await axios.post(
        `${API}/api/admin/lodr/backfill-red-flags?${params.toString()}`,
        null,
        { headers: authHeaders() },
      );
      setRedFlagReport(res.data);
      if (!dryRun) {
        const msg = includeInspector
          ? `Backfilled ${res.data.rows_updated} row(s) · ${res.data.inspector_calls_used} Gemini call(s)`
          : `Backfilled ${res.data.rows_updated} row(s) (Layer A only — 0 LLM cost)`;
        toast.success(msg);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRedFlagLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-data-cleanup-panel"
      className="rounded-xl border border-orange-500/30 bg-gradient-to-br from-orange-950/30 via-zinc-950/40 to-zinc-950/20 px-4 py-3 shadow-inner"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-left w-full"
        data-testid="admin-data-cleanup-toggle"
      >
        <Wrench className="w-4 h-4 text-orange-300" />
        <span className="font-heading text-sm font-semibold text-orange-100">
          Data Cleanup (v16cy)
        </span>
        <span className="ml-auto text-xs text-zinc-500">
          {open ? '▾' : '▸'}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          {/* How-to-use intro */}
          <div className="rounded bg-zinc-900/60 border border-zinc-800 px-3 py-2 text-[11px] text-zinc-400 leading-relaxed">
            <div className="text-orange-200 font-semibold mb-1">How to use this panel</div>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>
                See a wrong value on a row in the table below? Use{' '}
                <span className="text-orange-300 font-semibold">Step 1</span>{' '}
                (Re-extract a symbol) — works for 95 % of cases.
              </li>
              <li>
                See a duplicate row (same filing on BSE + NSE)? Use{' '}
                <span className="text-orange-300 font-semibold">Step 2</span>{' '}
                AFTER Step 1 has corrected the values.
              </li>
              <li>
                Big order (≥ ₹250 Cr) but customer column empty AND no
                bullishness? Use{' '}
                <span className="text-orange-300 font-semibold">Step 4</span>{' '}
                to populate the customer via LLM, then{' '}
                <span className="text-orange-300 font-semibold">Step 5</span>{' '}
                to force a Phase 2 verdict.
              </li>
              <li>
                Suspect a "fishy" BUY (related-party, oversized vs mcap,
                replaces cancelled contract)? Use{' '}
                <span className="text-orange-300 font-semibold">Step 7</span>{' '}
                to backfill the Red-Flag Inspector. Layer A is free; A+B
                adds Gemini on top BUYs (~₹0.05 / row).
              </li>
              <li>
                <span className="text-orange-300 font-semibold">Step 3</span>{' '}
                is a legacy one-time bulk cleanup for the old GYD$/AUD$ bug.
                You generally won&apos;t need it again.
              </li>
            </ol>
            <div className="mt-1.5 text-zinc-500">
              Each step has a <em className="text-zinc-300">Dry run</em>{' '}
              (preview, no writes) and an <em className="text-zinc-300">Apply</em>{' '}
              (commit). Always click Dry run first.
            </div>
          </div>

          {/* STEP 1 — Force re-extract a symbol (the main tool) */}
          <div className="space-y-2">
            <div className="text-xs font-mono text-orange-200">
              Step 1 — Re-extract a symbol (main tool)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Pick a symbol, click <strong>Dry run</strong> to see what would
              change, then <strong>Apply re-extract</strong> to commit. Works
              for wrong values caused by any extraction bug (currency
              confusion, cumulative-figure bleed, etc.). Zero LLM cost.
            </div>
            <div className="flex gap-2">
              <SymbolTypeahead
                value={forceSymbol}
                onChange={setForceSymbol}
                placeholder="Start typing an NSE symbol (e.g. ASHO…)"
                testIdPrefix="admin-force-symbol"
              />
              <button
                type="button"
                onClick={() => runForceReextract(true)}
                disabled={forceLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-force-dry-run"
              >
                {forceLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runForceReextract(false)}
                disabled={forceLoading || !forceReport || (forceReport.rows_updated === 0 && forceReport.report?.every((r) => r.action === 'unchanged'))}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-force-apply"
              >
                Apply re-extract
              </button>
            </div>
            {forceReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-32 overflow-auto">
                <div>scanned: {forceReport.candidates_scanned} · updated: {forceReport.rows_updated} · nulled: {forceReport.rows_nulled_for_llm_retry} · unchanged: {forceReport.rows_unchanged}{forceReport.errors?.length ? ` · errors: ${forceReport.errors.length}` : ''}</div>
                {(forceReport.report || []).slice(0, 6).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    {r.exchange}: ₹{r.old_value_cr} → {r.new_value_cr !== null ? `₹${r.new_value_cr}` : 'null'} [{r.action}{r.text_source ? ` · ${r.text_source}` : ''}]
                  </div>
                ))}
                {(forceReport.errors || []).slice(0, 3).map((e, i) => (
                  <div key={`err-${i}`} className="text-rose-400">
                    err [{e.phase}]: {e.err}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STEP 2 — Merge cross-exchange duplicates */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60">
            <div className="text-xs font-mono text-orange-200">
              Step 2 — Merge duplicate rows for a symbol
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Run this AFTER Step 1. Hides the lesser copy when the same
              filing appears on both BSE and NSE with the same value (±2 %).
            </div>
            <div className="flex gap-2">
              <SymbolTypeahead
                value={dedupeSymbol}
                onChange={setDedupeSymbol}
                placeholder="Start typing an NSE symbol"
                testIdPrefix="admin-dedupe-symbol"
              />
              <button
                type="button"
                onClick={() => runDedupe(true)}
                disabled={dedupeLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-dedupe-dry-run"
              >
                {dedupeLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runDedupe(false)}
                disabled={dedupeLoading || !dedupeReport || dedupeReport.merged_pairs === 0}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-dedupe-apply"
              >
                Apply merge
              </button>
            </div>
            {dedupeReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-32 overflow-auto">
                <div>scanned: {dedupeReport.candidates_scanned} · merged: {dedupeReport.merged_pairs}</div>
                {(dedupeReport.report || []).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    keep {r.kept_exchange} (₹{r.kept_value_cr}) · drop {r.dropped_exchange} (₹{r.dropped_value_cr}, {r.value_diff_pct}% diff)
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STEP 2b — Global dedup sweep (v17m) */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60" data-testid="admin-global-dedupe-card">
            <div className="text-xs font-mono text-orange-200">
              Step 2b — Global dedup sweep + leaderboard recalculate (v17m)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Runs a TWO-PASS dedupe across every symbol in the last N days:
              first by <strong>attachment_url</strong> (same PDF ingested
              twice — LLM value drift is ignored), then by{' '}
              <strong>value + customer</strong> bucket. Deletes losers so
              the Order Book Leaderboard recalculates rankings on next
              refresh. Use this when you see the same order counted twice
              (e.g. INDIANHUME × 2 for ₹738 Cr on 2026-07-03).
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <label className="text-[10px] text-zinc-500 flex items-center gap-1">
                Look-back days
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={gDedupeDays}
                  onChange={(e) => setGDedupeDays(Number(e.target.value) || 30)}
                  className="w-16 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-200 font-mono"
                  data-testid="admin-global-dedupe-days-input"
                />
              </label>
              <button
                type="button"
                onClick={() => runGlobalDedupe(true)}
                disabled={gDedupeLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-global-dedupe-dry-run"
              >
                {gDedupeLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runGlobalDedupe(false)}
                disabled={gDedupeLoading || !gDedupeReport || (gDedupeReport.duplicate_rows_identified === 0)}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-global-dedupe-apply"
              >
                Apply sweep · recalc leaderboard
              </button>
            </div>
            {gDedupeReport && (
              <div
                className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-56 overflow-auto"
                data-testid="admin-global-dedupe-report"
              >
                <div className="text-orange-200">
                  URL dups: {gDedupeReport.url_duplicates_identified || 0}
                  {' · '}
                  Value/customer dups: {(gDedupeReport.duplicate_rows_identified || 0) - (gDedupeReport.url_duplicates_identified || 0)}
                  {' · '}
                  Total identified: {gDedupeReport.duplicate_rows_identified || 0}
                  {' · '}
                  Deleted: {gDedupeReport.deleted || 0}
                  {' · '}
                  Dry: {String(gDedupeReport.dry_run)}
                </div>
                {(gDedupeReport.url_sample_groups || []).length > 0 && (
                  <div className="mt-1">
                    <div className="text-zinc-500 uppercase text-[9px]">URL-match sample</div>
                    {(gDedupeReport.url_sample_groups || []).slice(0, 6).map((g, i) => (
                      <div key={`u${i}`} className="text-zinc-400">
                        keep {String(g.winner_id).slice(0, 8)} (₹{g.winner_value_cr}) ·
                        drop {(g.loser_ids || []).map(x => String(x).slice(0, 8)).join(',')} (₹{(g.loser_values_cr || []).join(', ₹')})
                      </div>
                    ))}
                  </div>
                )}
                {(gDedupeReport.sample_groups || []).length > 0 && (
                  <div className="mt-1">
                    <div className="text-zinc-500 uppercase text-[9px]">Value/customer sample</div>
                    {(gDedupeReport.sample_groups || []).slice(0, 6).map((g, i) => (
                      <div key={`v${i}`} className="text-zinc-400">
                        {g.key}: keep {String(g.winner_id).slice(0, 8)} · drop {(g.loser_ids || []).map(x => String(x).slice(0, 8)).join(',')} (₹{g.value_cr})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* STEP 4 — Backfill customers for MAJOR-tier rows */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60">
            <div className="text-xs font-mono text-orange-200">
              Step 4 — Backfill customer / sector on MAJOR rows (v16co)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Finds rows ≥ ₹{majorMinValue} Cr in the last {majorDays} days
              with empty <code>customer</code> (regex-only extractions) and
              calls the LLM ONCE per row to populate customer / sector /
              summary, then clears Phase 2 so the next cron tick re-routes
              them as MAJOR. Cap LLM calls with the limit field.
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <label className="text-[10px] text-zinc-500 flex items-center gap-1">
                Days
                <input
                  type="number"
                  min={1}
                  max={180}
                  value={majorDays}
                  onChange={(e) => setMajorDays(Number(e.target.value) || 30)}
                  className="w-14 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-200 font-mono"
                  data-testid="admin-major-days-input"
                />
              </label>
              <label className="text-[10px] text-zinc-500 flex items-center gap-1">
                Min ₹Cr
                <input
                  type="number"
                  min={50}
                  step={50}
                  value={majorMinValue}
                  onChange={(e) => setMajorMinValue(Number(e.target.value) || 250)}
                  className="w-16 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-200 font-mono"
                  data-testid="admin-major-min-value-input"
                />
              </label>
              <label className="text-[10px] text-zinc-500 flex items-center gap-1">
                LLM limit
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={majorLimit}
                  onChange={(e) => setMajorLimit(Number(e.target.value) || 25)}
                  className="w-14 bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-zinc-200 font-mono"
                  data-testid="admin-major-limit-input"
                />
              </label>
              <button
                type="button"
                onClick={() => runMajorBackfill(true)}
                disabled={majorBackfillLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-major-backfill-dry-run"
              >
                {majorBackfillLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runMajorBackfill(false)}
                disabled={majorBackfillLoading || !majorBackfillReport || (majorBackfillReport.candidates_found === 0)}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-major-backfill-apply"
              >
                Apply backfill
              </button>
            </div>
            {majorBackfillReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-40 overflow-auto">
                <div>candidates: {majorBackfillReport.candidates_found} · updated: {majorBackfillReport.rows_updated}{majorBackfillReport.errors?.length ? ` · errors: ${majorBackfillReport.errors.length}` : ''}</div>
                {(majorBackfillReport.report || []).slice(0, 8).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    {r.symbol}: ₹{r.value_cr} Cr · {r.old_customer || '(empty)'} → {r.new_customer || '—'} [{r.action}]
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STEP 5 — Re-run Phase 2 LLM verdict for a symbol */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60">
            <div className="text-xs font-mono text-orange-200">
              Step 5 — Re-run Phase 2 verdict for a symbol (v16co)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Force-recomputes the bullishness verdict on every recent row
              for a symbol. Use AFTER Step 4 (or after Step 1 fixed the
              value) so the new customer / size flows into a fresh LLM
              verdict. ~3-5 sec + 1 LLM call per row.
            </div>
            <div className="flex gap-2">
              <SymbolTypeahead
                value={phase2RerunSymbol}
                onChange={setPhase2RerunSymbol}
                placeholder="Start typing an NSE symbol"
                testIdPrefix="admin-phase2-rerun-symbol"
              />
              <button
                type="button"
                onClick={() => runPhase2Rerun(true)}
                disabled={phase2RerunLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-phase2-rerun-dry-run"
              >
                {phase2RerunLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runPhase2Rerun(false)}
                disabled={phase2RerunLoading || !phase2RerunReport || phase2RerunReport.candidates_found === 0}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-phase2-rerun-apply"
              >
                Apply Phase 2 rerun
              </button>
            </div>
            {phase2RerunReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-40 overflow-auto">
                <div>candidates: {phase2RerunReport.candidates_found} · updated: {phase2RerunReport.rows_updated}{phase2RerunReport.errors?.length ? ` · errors: ${phase2RerunReport.errors.length}` : ''}</div>
                {(phase2RerunReport.report || []).slice(0, 8).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    ₹{r.value_cr} · {r.customer || '(no customer)'} · {r.old_verdict || '—'} → {r.new_verdict || '—'} [{r.action}]
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STEP 6 — Heal stale value_pct_of_ttm (Ashoka 936% bug) */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60">
            <div className="text-xs font-mono text-orange-200">
              Step 6 — Heal stale %TTM (v16cp)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Recomputes <code>value_pct_of_ttm</code> for any row whose
              stored percent drifted from <code>value_cr / ttm_cr</code> by
              &gt; 1 %. Fixes the legacy Ashoka 936 % display caused by
              re-extracts that updated value without refreshing the
              precomputed percent. Zero LLM cost · safe to run anytime.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => runHealStaleTtmPct(true)}
                disabled={healStaleLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-heal-stale-dry-run"
              >
                {healStaleLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runHealStaleTtmPct(false)}
                disabled={healStaleLoading || !healStaleReport || healStaleReport.candidates_found === 0}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-heal-stale-apply"
              >
                Apply heal
              </button>
            </div>
            {healStaleReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-40 overflow-auto">
                <div>candidates: {healStaleReport.candidates_found} · updated: {healStaleReport.rows_updated}</div>
                {(healStaleReport.sample || []).slice(0, 8).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    {r.symbol}: ₹{r.value_cr} / ₹{r.ttm_cr} → {r.old_pct === null ? '(null)' : `${r.old_pct}%`} → {r.new_pct}%
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STEP 7 — Red-Flag Inspector backfill (Vikran-style detection) */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60">
            <div className="text-xs font-mono text-orange-200">
              Step 7 — Backfill Red-Flag Inspector (v16cs)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Computes deterministic flags (oversized vs mcap / TTM, low
              confidence, sister cancellations, customer concentration) on
              every recent row WITHOUT re-running Phase 2. Optionally also
              runs Gemini Red-Flag Inspector on BUY + big rows (catches
              Vikran-style wholly-owned-subsidiary patterns). Leave symbol
              blank to backfill all symbols.
            </div>
            <div className="flex gap-2 items-center">
              <SymbolTypeahead
                value={redFlagSymbol}
                onChange={setRedFlagSymbol}
                placeholder="(optional) symbol"
                testIdPrefix="admin-redflag-symbol"
              />
              <button
                type="button"
                onClick={() => runRedFlagBackfill(true, false)}
                disabled={redFlagLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-redflag-dry-run"
              >
                {redFlagLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runRedFlagBackfill(false, false)}
                disabled={redFlagLoading || !redFlagReport || redFlagReport.candidates === 0}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-redflag-apply-det"
              >
                Apply Layer A only (free)
              </button>
              <button
                type="button"
                onClick={() => runRedFlagBackfill(false, true)}
                disabled={redFlagLoading || !redFlagReport || redFlagReport.candidates === 0}
                className="px-3 py-1.5 text-xs rounded border border-rose-500/60 bg-rose-500/20 hover:bg-rose-500/30 text-rose-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-redflag-apply-inspector"
              >
                Apply A + B (≤ 15 LLM)
              </button>
            </div>
            {redFlagReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-48 overflow-auto">
                <div>
                  candidates: {redFlagReport.candidates} · updated:{' '}
                  {redFlagReport.rows_updated} · inspector calls:{' '}
                  {redFlagReport.inspector_calls_used}
                </div>
                {(redFlagReport.report || []).slice(0, 12).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    {r.symbol}: ₹{r.value_cr} · {r.verdict} · flags=
                    {(r.det_flags || []).join(',') || '—'}
                    {r.inspector_score != null ? ` · LLM=${r.inspector_score}/10` : ''}{' '}
                    [{r.action}]
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STEP 9 — Bulk dedupe sweep (all symbols, v16cy) */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60">
            <div className="text-xs font-mono text-orange-200">
              Step 9 — Bulk dedupe sweep (v16cy · zero LLM cost)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Scans the last 30 days across ALL symbols and hides rows that
              share the same symbol + value (±2 %) within a ±2 h window
              (like the TEXRAIL ₹351.2 Cr case). Prefers rows with
              populated customer as keeper; ties broken by higher Phase 2
              confidence. Zero LLM cost — pure Mongo scan.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => runDedupeSweep(true)}
                disabled={sweepLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-sweep-dry-run"
              >
                {sweepLoading ? '…' : 'Dry run (all symbols)'}
              </button>
              <button
                type="button"
                onClick={() => runDedupeSweep(false)}
                disabled={sweepLoading || !sweepReport || sweepReport.duplicates_found === 0}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-sweep-apply"
              >
                Apply sweep
              </button>
            </div>
            {sweepReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-48 overflow-auto">
                <div>
                  scanned: {sweepReport.rows_scanned} rows /{' '}
                  {sweepReport.symbols_scanned} symbols · duplicates:{' '}
                  {sweepReport.duplicates_found} · hidden:{' '}
                  {sweepReport.rows_hidden}
                </div>
                {(sweepReport.sample || []).slice(0, 8).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    {r.symbol}: keeper={r.keeper_customer || '—'} · dropper=
                    {r.dropper_customer || '—'} · Δ{r.delta_minutes}min · ₹{r.value_cr}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* STEP 3 — Legacy bulk USD-prefix cleanup */}
          <div className="space-y-2 pt-3 border-t border-zinc-800/60">
            <div className="text-xs font-mono text-orange-200">
              Step 3 — Bulk fix for old GYD$/AUD$/CAD$ regex bug (one-time)
            </div>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Finds rows tagged with the legacy <code>usd_converted</code>{' '}
              pattern and value ≥ ₹5,000 Cr. Most installs no longer have
              any — this is just a safety-net sweep. Zero LLM cost.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => runUsdFix(true)}
                disabled={usdLoading}
                className="px-3 py-1.5 text-xs rounded border border-zinc-700 hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-40 font-mono"
                data-testid="admin-fix-usd-dry-run"
              >
                {usdLoading ? '…' : 'Dry run'}
              </button>
              <button
                type="button"
                onClick={() => runUsdFix(false)}
                disabled={usdLoading || !usdReport || usdReport.candidates_found === 0}
                className="px-3 py-1.5 text-xs rounded border border-orange-500/60 bg-orange-500/20 hover:bg-orange-500/30 text-orange-100 transition disabled:opacity-40 font-mono"
                data-testid="admin-fix-usd-apply"
              >
                Apply fix
              </button>
            </div>
            {usdReport && (
              <div className="text-[11px] font-mono text-zinc-300 bg-zinc-900/60 rounded p-2 max-h-32 overflow-auto">
                <div>candidates: {usdReport.candidates_found} · updated: {usdReport.rows_updated} · nulled: {usdReport.rows_nulled_for_llm_retry}</div>
                {(usdReport.report || []).slice(0, 4).map((r, i) => (
                  <div key={i} className="text-zinc-400">
                    {r.nse_symbol || r.bse_scrip_code}: ₹{r.old_value_cr} → {r.new_value_cr ? `₹${r.new_value_cr} (${r.new_pattern})` : 'null'}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};



// ==================================================================
// Main tab
// ==================================================================
const ALL_CUSTOMER_TYPES = [
  'PSU', 'GOVERNMENT_DEFENCE', 'GOVERNMENT_OTHER',
  'PRIVATE_DOMESTIC', 'PRIVATE_INTERNATIONAL', 'UNKNOWN',
];

const LODROrdersTab = ({ readOnly = false } = {}) => {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(14);
  const [symbol, setSymbol] = useState('');
  const [minValue, setMinValue] = useState('');
  const [minConfidence, setMinConfidence] = useState(30);
  const [hideNoValue, setHideNoValue] = useState(false);
  const [sizeTier, setSizeTier] = useState('ALL');
  const [verdictFilter, setVerdictFilter] = useState('ALL');
  const [hotStreakOnly, setHotStreakOnly] = useState(false);
  const [customerTypes, setCustomerTypes] = useState(
    () => [...ALL_CUSTOMER_TYPES],
  );
  const [busy, setBusy] = useState(false);

  const toggleCustomerType = useCallback((t) => {
    setCustomerTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }, []);

  // Session 147j+16y — client-side post-filter on top of the
  // backend-fetched rows so the user can refine the view without
  // forcing a re-fetch (most filters are visual quality gates).
  const filteredRows = React.useMemo(() => {
    const allow = new Set(customerTypes);
    const minConfRatio = (minConfidence || 0) / 100;
    // Session 147j+16ag — Size-tier filter using value_pct_of_ttm.
    // Threshold mapping mirrors the deterministic tier boundaries.
    const tierMin = {
      ALL: -Infinity,
      small_plus: 1,
      medium_plus: 5,
      large_plus: 15,
      huge: 30,
    }[sizeTier] ?? -Infinity;
    return (rows || []).filter((r) => {
      const llm = r?.llm_extracted || {};
      const conf = Number(llm.confidence) || 0;
      const needsReview = !r?.llm_extracted || llm._needs_review === true;
      if (conf < minConfRatio && !needsReview) return false;
      if (hideNoValue && (llm.order_value_cr == null)) return false;
      const ctype = llm.customer_type || 'UNKNOWN';
      if (!allow.has(ctype)) return false;
      if (sizeTier !== 'ALL') {
        const pct = Number(r?.value_pct_of_ttm);
        if (!Number.isFinite(pct)) return false;
        if (pct < tierMin) return false;
      }
      // Session 147j+16ak — verdict filter (BUY/WATCH/SKIP/AVOID)
      if (verdictFilter !== 'ALL') {
        const v = (r?.deep_analysis?.phase2?.verdict || '').toUpperCase();
        if (verdictFilter === 'PENDING') {
          if (v) return false;            // keep only rows with NO verdict
        } else if (v !== verdictFilter) {
          return false;
        }
      }
      // 147j+16aw — Hot streak filter: only rows where the company
      // had ≥2 prior orders in the last 30 days.
      if (hotStreakOnly) {
        const cnt = r?.deep_analysis?.phase1?.momentum?.['30d']?.count || 0;
        if (cnt < 2) return false;
      }
      return true;
    });
  }, [rows, customerTypes, minConfidence, hideNoValue, sizeTier, verdictFilter, hotStreakOnly]);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('days', String(days));
      if (symbol) params.set('symbol', symbol);
      if (minValue) params.set('min_value_cr', minValue);
      const [s, o] = await Promise.all([
        axios.get(`${API}/api/admin/lodr/stats?days=${days}`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/lodr/orders?${params.toString()}`, { headers: authHeaders() }),
      ]);
      setStats(s.data);
      setRows(o.data?.rows || []);
    } catch (e) {
      console.warn('[LODR] load failed', e?.response?.status || e?.message);
    }
  }, [days, symbol, minValue]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 1000); // poll every 30s
    return () => clearInterval(t);
  }, [load]);

  const onScan = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/lodr/scan-now?days_back=${days}`,
        {},
        { headers: authHeaders() },
      );
      toast.success('LODR scan triggered. Refreshing in a few seconds…');
      setTimeout(load, 5000);
    } catch (e) {
      toast.error(`Scan failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Session 147j+8 — historical backfill (Phase B.4)
  const [backfillState, setBackfillState] = useState(null);
  const loadBackfillState = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/lodr/backfill/status`,
        { headers: authHeaders() },
      );
      setBackfillState(r.data || null);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    loadBackfillState();
    const t = setInterval(loadBackfillState, 15 * 1000);
    return () => clearInterval(t);
  }, [loadBackfillState]);

  const onStartBackfill = async (months) => {
    if (!window.confirm(
      `Start ${months}-month LODR backfill?\n\n` +
      `Runs in background for ~6-8 hrs. Uses ~₹${Math.round(months * 15)} of Emergent credit.\n` +
      `You can keep using the app — backfill continues even if you close this tab.`,
    )) return;
    try {
      await axios.post(
        `${API}/api/admin/lodr/backfill/start?months_back=${months}`,
        {},
        { headers: authHeaders() },
      );
      toast.success(`${months}-month backfill started. Poll status below.`);
      loadBackfillState();
    } catch (e) {
      toast.error(`Backfill failed to start: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Session 147j+9 — Phase A.2 PDF re-extraction
  const [reextractState, setReextractState] = useState(null);
  const loadReextractState = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/lodr/reextract/status`,
        { headers: authHeaders() },
      );
      setReextractState(r.data || null);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    loadReextractState();
    const t = setInterval(loadReextractState, 10 * 1000);
    return () => clearInterval(t);
  }, [loadReextractState]);

  const onStartReextract = async () => {
    if (!window.confirm(
      `Re-extract null-value orders using PDFs?\n\n` +
      `Scans every existing corp_orders row where value is null + PDF is available, ` +
      `and re-runs LLM with the PDF body. Lifts extraction from ~50% → ~90%.\n\n` +
      `Cost: ~₹0.05 per row (≈ ₹100-200 total for a full backfilled DB).`,
    )) return;
    try {
      await axios.post(
        `${API}/api/admin/lodr/reextract/start`,
        {},
        { headers: authHeaders() },
      );
      toast.success('PDF re-extraction started.');
      loadReextractState();
    } catch (e) {
      toast.error(`Re-extract failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Session 147j+10 — Daily Pipeline scheduler status
  const [schedState, setSchedState] = useState(null);
  const loadSchedState = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/lodr/scheduler/status`,
        { headers: authHeaders() },
      );
      setSchedState(r.data || null);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    loadSchedState();
    const t = setInterval(loadSchedState, 20 * 1000);
    return () => clearInterval(t);
  }, [loadSchedState]);

  // Session 147j+15b — diagnose modal + TTM backfill state
  const [diagRow, setDiagRow] = useState(null);
  const [ttmBackfillState, setTtmBackfillState] = useState(null);
  const loadTtmBackfillState = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/lodr/backfill-ttm/status`,
        { headers: authHeaders() },
      );
      setTtmBackfillState(r.data || null);
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    loadTtmBackfillState();
    const t = setInterval(loadTtmBackfillState, 10 * 1000);
    return () => clearInterval(t);
  }, [loadTtmBackfillState]);

  const onStartTtmBackfill = async () => {
    if (!window.confirm(
      'Backfill % of TTM for all rows where it is currently null?\n\n' +
      'For each row with an order value but missing TTM, hits yfinance ' +
      '(cached for 14 days) to get the company\'s trailing-twelve-month ' +
      'revenue and computes value_pct_of_ttm.\n\n' +
      'Free — no LLM cost. Runs in background, ~5-10 min for typical DB.',
    )) return;
    try {
      await axios.post(
        `${API}/api/admin/lodr/backfill-ttm`,
        {},
        { headers: authHeaders() },
      );
      toast.success('TTM backfill started.');
      loadTtmBackfillState();
    } catch (e) {
      toast.error(`Backfill failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onRunPipelineNow = async () => {
    if (!window.confirm(
      'Run the full daily pipeline NOW?\n\n' +
      'Chains: LODR scan → data fetch → scoring → rebalance across ' +
      'all 3 profiles (Largecap, Hunter, ValueHunter).\n\n' +
      'Takes ~15-30 minutes. You can keep using the app.',
    )) return;
    try {
      await axios.post(
        `${API}/api/admin/lodr/scheduler/run-now`,
        {},
        { headers: authHeaders() },
      );
      toast.success('Daily pipeline started.');
      loadSchedState();
    } catch (e) {
      toast.error(`Pipeline failed to start: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="lodr-tab">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground tracking-tight flex items-center gap-2">
          <FileText className="w-6 h-6 text-violet-400" />
          LODR Order Book
          <span className="px-2 py-0.5 text-[10px] font-mono uppercase border border-violet-500/40 bg-violet-500/10 text-violet-300 rounded">
            Phase A
          </span>
        </h2>
        <p className="text-xs text-muted-foreground font-mono mt-1">
          SEBI Reg 30 "Award of Order / Receipt of Order" announcements
          from BSE + NSE, structured by gpt-4o-mini.{' '}
          <span className="text-emerald-300">
            Intraday cron every 30 min, 7 days/week.
          </span>
        </p>
      </div>

      {/* Session 147j+16ab — Intraday LODR cron heartbeat panel (admin only) */}
      {/* Session 147j+16bo Ship 2 — cron heartbeat + run-now hidden in read-only mode */}
      {!readOnly && <IntradayCronPanel stats={stats} />}

      {/* 147j+17k — Persistent pipeline health banner. Shows if Order
          Wins is running, why it failed (credit vs syntax), and the
          current LLM credit balance. Admin-only. */}
      {!readOnly && <CronHealthBanner />}

      {!readOnly && (
        <DailyPipelinePanel
          state={schedState}
          onRunNow={onRunPipelineNow}
        />
      )}

      <StatsBar stats={stats} />

      {/* 147j+16dm — Manipulation-risk banner. Fires when any CRITICAL
          flag exists in the currently loaded window. Visible to
          everyone (users + admin) — this is the whole point. */}
      {(() => {
        const critCount = (rows || []).filter((r) => r?.manipulation_risk?.flag === 'CRITICAL').length;
        const highCount = (rows || []).filter((r) => r?.manipulation_risk?.flag === 'HIGH').length;
        if (critCount === 0 && highCount === 0) return null;
        return (
          <div
            className="border-2 border-rose-500/60 bg-gradient-to-r from-rose-500/15 to-orange-500/10 rounded p-3 flex items-start gap-3"
            data-testid="lodr-manipulation-risk-banner"
          >
            <div className="text-2xl">🚨</div>
            <div className="flex-1">
              <div className="text-rose-100 font-bold text-sm">
                Manipulation-Risk Alert
              </div>
              <div className="text-rose-200/90 text-[11px] mt-0.5">
                {critCount > 0 && (
                  <span><b>{critCount} CRITICAL</b> order{critCount > 1 ? 's' : ''} — a huge order in a stock that barely trades.</span>
                )}
                {critCount > 0 && highCount > 0 && ' · '}
                {highCount > 0 && (
                  <span><b>{highCount} HIGH</b> risk order{highCount > 1 ? 's' : ''} — order dwarfs daily turnover.</span>
                )}
                <span className="ml-1 text-rose-300/70">
                  Prime pump-and-dump territory — verify liquidity + delivery history before trading.
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 147j+16ce/cf — Phase 2 cron + LODR scanner pause/resume.
          Admin-only. Lives above the filter bar so a runaway-burn
          incident can be stopped with one click without SSH. */}
      {!readOnly && (
        <>
          <Phase2CronToggle />
          <LodrScannerToggle />
          <LlmSpendPanel />
          <DataCleanupPanel />
        </>
      )}

      <FilterBar
        days={days}
        setDays={setDays}
        minValue={minValue}
        setMinValue={setMinValue}
        symbol={symbol}
        setSymbol={setSymbol}
        minConfidence={minConfidence}
        setMinConfidence={setMinConfidence}
        hideNoValue={hideNoValue}
        setHideNoValue={setHideNoValue}
        customerTypes={customerTypes}
        toggleCustomerType={toggleCustomerType}
        sizeTier={sizeTier}
        setSizeTier={setSizeTier}
        verdictFilter={verdictFilter}
        setVerdictFilter={setVerdictFilter}
        hotStreakOnly={hotStreakOnly}
        setHotStreakOnly={setHotStreakOnly}
        onScan={onScan}
        busy={busy}
        readOnly={readOnly}
      />

      <div
        className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground"
        data-testid="lodr-filter-summary"
      >
        Showing {filteredRows.length} of {rows.length} rows
        {(minConfidence > 0 || hideNoValue || customerTypes.length < ALL_CUSTOMER_TYPES.length) && (
          <span className="text-emerald-400"> • filters active</span>
        )}
      </div>

      <OrdersTable
        rows={filteredRows}
        onDiagnose={setDiagRow}
        readOnly={readOnly}
        onRowRefresh={(fresh) => {
          // Session 147j+17d — When the drawer refetches a row and its
          // phase2 verdict arrives, propagate the fresh row back into
          // the parent list so the closed-drawer table cell (verdict
          // chip, red-flag chip, EXTR/VERD %) re-renders immediately
          // instead of staying stuck on the initial "-" state.
          if (!fresh || !fresh._id) return;
          setRows((prev) => prev.map((r) => (r._id === fresh._id ? { ...r, ...fresh } : r)));
        }}
      />

      {/* Session 147j+15b — Diagnose modal — admin-only (has force-reextract POST). */}
      {!readOnly && diagRow && (
        <DiagnoseModal
          row={diagRow}
          onClose={() => setDiagRow(null)}
          onRefresh={load}
        />
      )}

      {/* Top recipients leaderboard */}
      {stats?.top_recipients?.length > 0 && (
        <div className="border border-border bg-surface" data-testid="lodr-top-recipients">
          <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-wider flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3 text-emerald-300" />
            Top recipients · last {stats.days}d
          </div>
          <table className="w-full text-[11px] font-mono">
            <thead className="bg-surface-highlight">
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-1.5 text-left">Symbol</th>
                <th className="px-3 py-1.5 text-right">Total ₹Cr</th>
                <th className="px-3 py-1.5 text-right">Orders</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_recipients.map((r) => (
                <tr key={r.symbol} className="border-b border-border/40">
                  <td className="px-3 py-1.5 font-bold text-foreground">{r.symbol}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-300">
                    <IndianRupee className="w-3 h-3 inline mr-0.5" />
                    {Number(r.total_value_cr).toFixed(1)}
                  </td>
                  <td className="px-3 py-1.5 text-right text-foreground">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Session 147j+8 — Phase B.4 historical backfill (admin-only) */}
      {!readOnly && (<>
      <div
        className="border border-border bg-surface p-3"
        data-testid="lodr-backfill-panel"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-foreground flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-cyan-300" />
              Historical Backfill · Phase B.4
            </div>
            <div className="text-[10px] text-muted-foreground font-mono mt-1 max-w-2xl">
              One-time retroactive scan of BSE "Award_of_Order" filings so
              book-to-bill has a full TTM denominator from day 1. Runs in
              background for ~6-8 hrs. ~₹150-300 of Emergent credit total.
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <button
              data-testid="lodr-backfill-12m-btn"
              onClick={() => onStartBackfill(12)}
              disabled={backfillState?.status === 'running'}
              className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Run 12-month backfill
            </button>
            <button
              data-testid="lodr-backfill-6m-btn"
              onClick={() => onStartBackfill(6)}
              disabled={backfillState?.status === 'running'}
              className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider border border-border text-muted-foreground hover:bg-surface-highlight disabled:opacity-50 disabled:cursor-not-allowed"
            >
              6-month (cheaper)
            </button>
          </div>
        </div>
        {backfillState && backfillState.status !== 'idle' && (
          <div
            className="mt-2 pt-2 border-t border-border font-mono text-[10px] grid grid-cols-2 md:grid-cols-5 gap-2"
            data-testid="lodr-backfill-progress"
          >
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Status</div>
              <div className={
                backfillState.status === 'running' ? 'text-cyan-300'
                  : backfillState.status === 'done' ? 'text-emerald-300'
                    : backfillState.status === 'error' ? 'text-rose-300'
                      : 'text-foreground'
              }>
                {backfillState.status}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Chunks</div>
              <div className="text-foreground">
                {backfillState.chunks_done || 0} / {backfillState.chunks_total || 0}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Fetched</div>
              <div className="text-foreground">{backfillState.orders_fetched || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">New</div>
              <div className="text-emerald-300">{backfillState.orders_new || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Skipped</div>
              <div className="text-amber-300">{backfillState.orders_skipped || 0}</div>
            </div>
            {backfillState.current_chunk && (
              <div className="col-span-2 md:col-span-5">
                <div className="text-muted-foreground uppercase tracking-wider">Current chunk</div>
                <div className="text-foreground">{backfillState.current_chunk}</div>
              </div>
            )}
            {backfillState.last_error && (
              <div className="col-span-2 md:col-span-5">
                <div className="text-muted-foreground uppercase tracking-wider">Last error</div>
                <div className="text-rose-300">{backfillState.last_error}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Educational footer */}
      <div
        className="border border-border bg-surface p-3"
        data-testid="lodr-reextract-panel"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-foreground flex items-center gap-1.5">
              <RefreshCw className="w-3 h-3 text-amber-300" />
              PDF Re-extraction · Phase A.2
            </div>
            <div className="text-[10px] text-muted-foreground font-mono mt-1 max-w-2xl">
              Walks all existing orders where the LLM returned NULL value (headline had no ₹ number)
              and re-runs with the attached PDF body. Lifts extraction rate ~50% → ~90%. Zero BSE calls.
            </div>
          </div>
          <button
            data-testid="lodr-reextract-start-btn"
            onClick={onStartReextract}
            disabled={reextractState?.is_running}
            className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Re-extract null values
          </button>
        </div>
        {reextractState && (reextractState.is_running || reextractState.processed > 0) && (
          <div
            className="mt-2 pt-2 border-t border-border font-mono text-[10px] grid grid-cols-2 md:grid-cols-5 gap-2"
            data-testid="lodr-reextract-progress"
          >
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Status</div>
              <div className={reextractState.is_running ? 'text-amber-300' : 'text-foreground'}>
                {reextractState.is_running ? 'running' : (reextractState.finished_at ? 'done' : 'idle')}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Candidates</div>
              <div className="text-foreground">{reextractState.total_candidates || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Processed</div>
              <div className="text-foreground">{reextractState.processed || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Upgraded</div>
              <div className="text-emerald-300">{reextractState.upgraded || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Still null</div>
              <div className="text-rose-300">{reextractState.still_null || 0}</div>
            </div>
            <div title="BSE served HTML error pages (expired PDF URLs). Unrecoverable — data retention limit on BSE's side.">
              <div className="text-muted-foreground uppercase tracking-wider">Expired PDFs</div>
              <div className="text-amber-300">{reextractState.expired_pdfs || 0}</div>
            </div>
            <div title="OCR fallback recovered text from image-scan PDFs.">
              <div className="text-muted-foreground uppercase tracking-wider">OCR recovered</div>
              <div className="text-emerald-300">{reextractState.ocr_recovered || 0}</div>
            </div>
          </div>
        )}
      </div>

      {/* Session 147j+15b — TTM Backfill panel (% of TTM) */}
      <div
        className="border border-border bg-surface p-3"
        data-testid="lodr-ttm-backfill-panel"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-foreground flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3 text-cyan-300" />
              % of TTM Backfill · Phase A.3
            </div>
            <div className="text-[10px] text-muted-foreground font-mono mt-1 max-w-2xl">
              Walks rows where order value is set but `% of TTM` is null
              (typically small/recently-listed names not in the AlphaScout
              universe). Hits yfinance directly for TTM revenue. Cached
              14d in `lodr_ttm_cache`. Free — no LLM cost.
            </div>
          </div>
          <button
            data-testid="lodr-ttm-backfill-btn"
            onClick={onStartTtmBackfill}
            disabled={ttmBackfillState?.is_running}
            className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Backfill % of TTM
          </button>
        </div>
        {ttmBackfillState && (ttmBackfillState.is_running || ttmBackfillState.processed > 0) && (
          <div
            className="mt-2 pt-2 border-t border-border font-mono text-[10px] grid grid-cols-2 md:grid-cols-5 gap-2"
            data-testid="lodr-ttm-backfill-progress"
          >
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Status</div>
              <div className={ttmBackfillState.is_running ? 'text-amber-300' : 'text-foreground'}>
                {ttmBackfillState.is_running ? 'running' : (ttmBackfillState.finished_at ? 'done' : 'idle')}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Candidates</div>
              <div className="text-foreground">{ttmBackfillState.total_candidates || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Processed</div>
              <div className="text-foreground">{ttmBackfillState.processed || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">% filled</div>
              <div className="text-emerald-300">{ttmBackfillState.pct_filled || 0}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider">Still null</div>
              <div className="text-rose-300">{ttmBackfillState.still_null || 0}</div>
            </div>
            {ttmBackfillState.last_symbol && (
              <div>
                <div className="text-muted-foreground uppercase tracking-wider">Last symbol</div>
                <div className="text-foreground">{ttmBackfillState.last_symbol}</div>
              </div>
            )}
          </div>
        )}
      </div>
      </>)}

      {/* Educational footer (admin-only — mentions internal phase names) */}
      {!readOnly && (
      <div className="border border-border bg-surface p-3 text-[10px] font-mono text-muted-foreground" data-testid="lodr-legend">
        <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-foreground">
          <AlertCircle className="w-3 h-3" /> What this captures
        </div>
        <ul className="space-y-1 list-disc list-inside">
          <li>SEBI mandates disclosure within 24-30h for orders ≥ 2% of TTM revenue</li>
          <li>2023 LODR amendment created the "Award_of_Order_Receipt_of_Order" sub-category — clean filter, zero false positives</li>
          <li>LLM extraction may return confidence &lt; 0.6 when value isn't in the headline (PDF parsing for those is Phase A.2)</li>
          <li>Best alpha for capital-goods / defence / EPC / infra / IT services. Banks/FMCG/pharma rarely fire</li>
        </ul>
      </div>
      )}
    </div>
  );
};

export default LODROrdersTab;
